<template>
  <div class="pos">
    <header class="pos__header">
      <div class="pos__brand">
        <span class="pos__brand-mark">S</span>
        <div>
          <h1>Sauda</h1>
          <p v-if="isQuickSaleMode">{{ $t('cash.header.quickSale') }}</p>
          <p v-else>{{ $t('cash.header.parallelSales') }}</p>
      </div>
    </div>

      <div class="pos__header-meta">
        <label class="pos__paper">
          <span>{{ $t('cash.header.paperWidth') }}</span>
          <select
            id="pos-paper-width"
            name="paper_width"
            v-model.number="paperWidth"
            @change="onPaperWidthChange"
          >
            <option :value="58">58 мм</option>
            <option :value="80">80 мм</option>
          </select>
        </label>
        <label
          class="pos__touch-toggle"
          :class="{ 'is-on': touchMode }"
          :title="touchMode ? $t('cash.header.touchScreenOn') : $t('cash.header.touchScreenOff')"
        >
          <input
            type="checkbox"
            :checked="touchMode"
            @change="toggleTouchMode"
          >
          <span>{{ $t('cash.header.touchScreen') }}</span>
        </label>
        <button
          type="button"
          class="pos__scale-live"
          :class="{
            'pos__scale-live--ok': scaleLive && scaleLive.stable && scaleLive.value > 0,
            'pos__scale-live--wait': scaleLive && (!scaleLive.stable || scaleLive.value <= 0),
            'pos__scale-live--off': !scaleLive,
          }"
          :title="$t('cash.header.scale')"
          @click="refreshScaleNow"
        >
          <i class="mdi mdi-scale-bathroom"></i>
          <span>{{ scaleLiveText }}</span>
        </button>
        <span v-if="cashierName" class="pos__cashier">
          <i class="mdi mdi-account-circle-outline"></i>
          {{ cashierName }}
        </span>
        <span class="pos__live">
          <i></i>
          {{ $t('cash.header.online') }}
        </span>
          </div>
      <div class="pos__header-actions">
        <button
          v-if="canOpenAdmin"
          type="button"
          class="pos__icon-btn"
          :title="$t('cash.actions.adminPanel')"
          @click="$router.push('/admin_panel')"
        >
          <i class="mdi mdi-view-dashboard-outline"></i>
        </button>
        <button type="button" class="pos__icon-btn" :title="$t('cash.actions.logout')" @click="logout">
          <i class="mdi mdi-logout"></i>
        </button>
        <LanguageSwitcher dark class="pos__language-switcher" />
      </div>
    </header>

    <div class="pos__body">
      <section v-if="!isQuickSaleMode" class="pos__catalog">
        <div class="pos__panel">
          <div class="pos__section-title">{{ $t('cash.catalog.categories') }}</div>
          <div class="pos__chips">
            <button
              v-for="category in categories"
              :key="category.id"
              type="button"
              class="pos__chip"
              :class="{ 'pos__chip--active': category_id === category.id && !search }"
              :disabled="productsLoading"
              @click="selectCategory(category.id)"
            >
              {{ category.name }}
            </button>
        </div>

          <div class="pos__section-head">
            <div class="pos__section-title pos__section-title--inline">{{ $t('cash.catalog.products') }}</div>
            <div class="pos__catalog-actions">
              <div class="pos__search-wrap">
                <i class="mdi mdi-magnify"></i>
        <input
                  id="pos-product-search"
                  name="product_search"
                  ref="productSearch"
            v-model="search"
                  class="pos__search"
            type="text"
                  readonly
                  inputmode="none"
                  :placeholder="$t('cash.catalog.searchPlaceholder')"
                  @click="openSearchKeyboard"
                >
                <button
                  type="button"
                  class="pos__search-kb-btn"
                  :title="$t('cash.searchKeyboard.open')"
                  @click="openSearchKeyboard"
                >
                  <i class="mdi mdi-keyboard-outline"></i>
                </button>
              </div>
              <button
                type="button"
                class="pos__camera-btn"
                :title="$t('cash.scanner.openCamera')"
                @click="openCameraScanner"
              >
                <i class="mdi mdi-barcode-scan"></i>
              </button>
            </div>
          </div>

          <div v-if="productsLoading && !products.length" class="pos__empty">
            <i class="mdi mdi-loading mdi-spin"></i>
            <p>{{ $t('cash.catalog.loadingProducts') }}</p>
          </div>
          <div v-else-if="products.length" class="pos__products" :class="{ 'pos__products--loading': productsLoading }">
            <button
              v-for="product in products"
              :key="product.id"
              type="button"
              class="pos__product"
              @click="addOrder(product)"
          >
              <span class="pos__product-top">
                <span class="pos__product-info">
                  <img
                    v-if="product.image_thumb_url || product.image_url"
                    class="pos__product-image"
                    :src="product.image_thumb_url || product.image_url"
                    :alt="product.name"
                  >
                  <span class="pos__product-text">
                    <span class="pos__product-name">{{ product.name }}</span>
                    <span v-if="productByproductsLabel(product)" class="pos__product-together">
                      {{ $t('cash.catalog.soldTogether') }}: {{ productByproductsLabel(product) }}
                    </span>
                  </span>
                </span>
                <span class="pos__product-add">
                  <i class="mdi mdi-plus"></i>
                </span>
              </span>
              <span class="pos__product-meta">
                <span class="pos__product-price">{{ product.price }} ₸</span>
                <span class="pos__product-stock">{{ $t('cash.catalog.inStock', { quantity: productAvailableQty(product) }) }}</span>
              </span>
            </button>
          </div>
          <div v-else class="pos__empty">
            <i class="mdi mdi-package-variant-closed"></i>
            <p>{{ search ? $t('cash.catalog.noResults') : $t('cash.catalog.noProductsSelected') }}</p>
            <span>{{ search ? $t('cash.catalog.tryAnotherSearch') : $t('cash.catalog.selectCategoryOrSearch') }}</span>
        </div>

        <v-pagination
            v-if="pageCount > 1 && !search"
            v-model="page"
            :length="pageCount"
            :total-visible="5"
            color="#0b4f8a"
            class="mt-4"
          />
      </div>
      </section>

      <section v-else class="pos__catalog">
        <div class="pos__panel pos__quick">
          <div class="pos__section-title">{{ $t('cash.quickSale.title') }}</div>
          <p class="pos__quick-hint">
            {{ $t('cash.quickSale.hint') }}
          </p>
          <label class="pos__quick-amount">
            <span>{{ $t('cash.quickSale.amountLabel') }}</span>
            <input
              id="pos-quick-amount"
              name="quick_amount"
              :value="amount_of_order || ''"
              type="number"
              min="0"
              step="1"
              inputmode="decimal"
              placeholder="0"
              @input="onQuickAmountInput"
            >
          </label>
          <ul class="pos__quick-points">
            <li><i class="mdi mdi-check-circle"></i> {{ $t('cash.quickSale.paymentTypeHint') }}</li>
            <li><i class="mdi mdi-check-circle"></i> {{ $t('cash.quickSale.bonusHint') }}</li>
            <li><i class="mdi mdi-check-circle"></i> {{ $t('cash.quickSale.loanHint') }}</li>
          </ul>
        </div>
      </section>

      <aside class="pos__cart" ref="cartSection">
        <div class="pos__cart-shell">
          <div class="pos__tabs">
            <div class="pos__tabs-list">
              <button
                v-for="(sale, index) in sales"
                :key="sale.id"
                type="button"
                class="pos__tab"
                :class="{ 'pos__tab--active': sale.id === activeSaleId }"
                @click="switchSale(sale.id)"
              >
                <span>{{ saleTitle(sale, index) }}</span>
                <em v-if="sale.orders.length">{{ sale.orders.length }}</em>
                <i
                  v-if="sales.length > 1"
                  class="mdi mdi-close"
                  @click.stop="closeSale(sale.id)"
                ></i>
              </button>
            </div>
            <button
              type="button"
              class="pos__tab-add"
              :title="$t('cash.sales.newSale')"
              @click="addSale"
            >
              <i class="mdi mdi-plus"></i>
            </button>
          </div>

          <div class="pos__cart-head">
            <div>
              <h2>{{ isQuickSaleMode ? $t('cash.payment.title') : $t('cash.cart.currentReceipt') }}</h2>
              <p v-if="isQuickSaleMode">{{ $t('cash.payment.amountAndMethod') }}</p>
              <p v-else>{{ $t('cash.cart.addProductsLeft') }}</p>
            </div>
            <span v-if="!isQuickSaleMode && orders.length" class="pos__cart-count">{{ $t('cash.cart.positions', { count: orders.length }) }}</span>
          </div>

          <div v-if="isQuickSaleMode" class="pos__quick-total">
            <span>{{ $t('cash.payment.due') }}</span>
            <strong>{{ Number(amount_of_order) || 0 }} ₸</strong>
          </div>

          <div v-else-if="!orders.length" class="pos__cart-empty">
            <i class="mdi mdi-cart-outline"></i>
            <p>{{ $t('cash.cart.empty') }}</p>
            <span>{{ $t('cash.cart.emptyHint') }}</span>
          </div>

          <div v-else class="pos__cart-list">
            <article
              v-for="(order, i) in orders"
              :key="order.line_uid || order.id || i"
              class="pos__cart-item"
            >
              <div class="pos__cart-item-top">
                <div>
                  <h3>{{ order.name }}</h3>
                  <p v-if="order.byproducts_label" class="pos__cart-together">
                    {{ $t('cash.catalog.soldTogether') }}: {{ order.byproducts_label }}
                  </p>
                  <p v-if="order.modifiers && order.modifiers.length" class="pos__cart-mods">
                    <span v-for="mod in order.modifiers" :key="mod.id">+ {{ mod.name }}</span>
                  </p>
                  <p class="pos__cart-unit">
                    <s v-if="order.display_line_discount" class="pos__cart-old-price">{{ order.display_unit_base || order.price }}</s>
                    {{ order.display_unit_price != null ? order.display_unit_price : order.price }}
                    <span v-if="order.count_type === 'count'">{{ $t('cash.cart.unitCount') }}</span>
                    <span v-else-if="order.count_type === 'kg'">{{ $t('cash.cart.unitKg') }}</span>
                    <span v-else-if="order.count_type === 'litr'">{{ $t('cash.cart.unitLiter') }}</span>
                  </p>
                </div>
                <button type="button" class="pos__remove" @click="deleteElement(order.line_uid || order.id)">
                  <i class="mdi mdi-close"></i>
                </button>
              </div>

              <div v-if="discounts.length" class="pos__discount">
                <i class="mdi mdi-sale-outline"></i>
                <div class="pos__discount-chips">
                  <button
                    type="button"
                    class="pos__discount-chip"
                    :class="{ 'is-active': !order.discount_id }"
                    @click="applyDiscount(order, '')"
                  >
                    {{ $t('cash.discount.none') }}
                  </button>
                  <button
                    v-for="d in discounts"
                    :key="d.id"
                    type="button"
                    class="pos__discount-chip"
                    :class="{ 'is-active': Number(order.discount_id) === Number(d.id) }"
                    :title="`${d.name} (${discountShort(d)})`"
                    @click="applyDiscount(order, d.id)"
                  >
                    {{ d.name }}
                    <em>{{ discountShort(d) }}</em>
                  </button>
                </div>
                <span v-if="order.display_line_discount" class="pos__discount-saved">
                  −{{ order.display_line_discount }} ₸
                </span>
              </div>

              <div class="pos__cart-item-bottom">
                <div v-if="order.count_type === 'count'" class="pos__qty">
                  <button type="button" @click="subOrderCount(order)">−</button>
                  <span>{{ order.count_value ? order.count_value : 1 }}</span>
                  <button type="button" @click="addOrderCount(order)">+</button>
                </div>
                <div v-else class="pos__qty pos__qty--input">
                  <input
                    :id="`pos-weight-${order.line_uid || order.id}`"
                    :name="`weight_${order.line_uid || order.id}`"
                    v-model="order.amount_value"
                    :placeholder="$t('cash.cart.weightOrLiter')"
                    type="number"
                    step="any"
                    inputmode="decimal"
                    data-touch-decimal="1"
                    @input="onCartWeightInput(order)"
                  >
                  <button
                    type="button"
                    class="pos__scale-btn"
                    :title="$t('cash.scale.read')"
                    :disabled="scaleLoadingId === (order.line_uid || order.id)"
                    @click="readWeightForOrder(order)"
                  >
                    <i class="mdi" :class="scaleLoadingId === (order.line_uid || order.id) ? 'mdi-loading mdi-spin' : 'mdi-scale'"></i>
                    {{ $t('cash.scale.label') }}
                  </button>
                </div>

                <p v-if="order.order_price" class="pos__cart-sum">{{ order.order_price }} ₸</p>
              </div>
            </article>
          </div>

          <div v-if="orders.length || isQuickSaleMode" class="pos__checkout">
            <div class="pos__bonus">
              <v-switch
                v-model="give_bonus"
                color="#0b4f8a"
                :label="$t('cash.bonus.accrue')"
                hide-details
                dense
              />

              <div v-if="give_bonus" class="pos__bonus-search">
                <input
                  id="pos-bonus-phone"
                  name="bonus_phone"
                  class="pos__phone"
                  :value="phone"
                  type="text"
                  inputmode="numeric"
                  :placeholder="$t('cash.bonus.phoneOrCard')"
                  @input="onBonusSearchInput"
                >
                <button
                  type="button"
                  class="pos__ghost-btn pos__bonus-register-btn"
                  @click="openBonusClientModal()"
                >
                  {{ $t('cash.bonus.newClient') }}
                </button>
              </div>

              <div
                v-if="bonusClient"
                class="pos__bonus-client"
              >
                <div>
                  <strong>{{ bonusClient.name }}</strong>
                  <p>
                    {{ formatBonusPhone(bonusClient.phone) }}
                    <span v-if="bonusClient.card_number"> · {{ $t('cash.bonus.card', { number: bonusClient.card_number }) }}</span>
                  </p>
                </div>
                <em>{{ $t('cash.bonus.balance', { amount: Number(bonusClient.bonus) || 0 }) }}</em>
              </div>

              <p v-if="bonusNotFound" class="pos__bonus-miss">
                {{ $t('cash.bonus.clientNotFound') }} —
                <button type="button" class="pos__link-btn" @click="openBonusClientModal()">
                  {{ $t('cash.actions.add') }}
                </button>
              </p>

              <div
                v-if="bonusClient && Number(bonusClient.bonus) > 0"
                class="pos__bonus-actions"
              >
                <button type="button" class="pos__ghost-btn" @click="subBonus(bonusClient.bonus)">
                  {{ $t('cash.bonus.redeemAll') }}
                </button>
                <button type="button" class="pos__ghost-btn" @click="show_bonus = true">
                  {{ $t('cash.bonus.redeemPart') }}
                </button>
            </div>
            </div>

            <div v-if="hasLoanPayment" class="pos__loan-summary" @click="show_loan = true">
              <div class="pos__loan-summary-icon">
                <i class="mdi mdi-hand-coin-outline"></i>
              </div>
              <div class="pos__loan-summary-body">
                <strong>{{ name || $t('cash.loan.title') }}</strong>
                <p v-if="loanDraftComplete">
                  {{ formatLoanAmount(loan) }} · {{ formatLoanPhone(phone_loan) }}
                  <span v-if="date"> · {{ $t('cash.loan.until', { date: formatLoanDate(date) }) }}</span>
                </p>
                <p v-else>{{ $t('cash.loan.fillDebtorDetails') }}</p>
              </div>
              <button type="button" class="pos__loan-summary-edit" @click.stop="show_loan = true">
                <i class="mdi mdi-pencil-outline"></i>
              </button>
            </div>

            <div class="pos__checkout-footer">
              <div class="pos__payment">
                <div class="pos__payment-label">{{ $t('cash.payment.type') }}</div>
                <div class="pos__pay-types">
                  <button
                    v-for="type in payment_types"
                    :key="type.value"
                    type="button"
                    class="pos__pay-type"
                    :class="{ 'is-active': isPaymentTypeSelected(type.value) }"
                    @click="togglePaymentType(type.value)"
                  >
                    {{ type.label }}
                  </button>
                </div>

                <template v-for="(p, p_index) in payments">
                  <div
                    v-if="shouldShowPaymentAmount(p)"
                    :key="`amount-${p_index}-${p.selected_payment}`"
                    class="pos__payment-amount"
                  >
                    <span class="pos__payment-amount-name">{{ paymentTypeLabel(p.selected_payment) }}</span>
                    <button
                      v-if="touchMode"
                      type="button"
                      class="pos__payment-amount-btn"
                      @click="openPaymentPad(p_index)"
                    >
                      <span :class="{ 'is-placeholder': !Number(p.amount) }">
                        {{ Number(p.amount) ? formatReceiptMoney(p.amount) : '0' }}
                      </span>
                      <i class="mdi mdi-dialpad"></i>
                    </button>
                    <input
                      v-else
                      class="pos__payment-amount-input"
                      type="number"
                      min="0"
                      step="1"
                      inputmode="numeric"
                      :value="p.amount || ''"
                      placeholder="0"
                      @input="onPaymentAmountTyping(p_index, $event)"
                    >
                  </div>
                </template>
              </div>

              <div class="pos__total pos__total--compact">
                <span>{{ $t('cash.payment.due') }}</span>
                <div class="pos__due-edit">
                  <button
                    v-if="touchMode"
                    type="button"
                    class="pos__due-edit-btn"
                    @click="openDuePad"
                  >
                    {{ formatReceiptMoney(amount_of_order) }}
                    <i class="mdi mdi-pencil-outline"></i>
                  </button>
                  <input
                    v-else
                    class="pos__due-input"
                    type="number"
                    min="0"
                    step="1"
                    inputmode="numeric"
                    :value="amount_of_order || ''"
                    @change="onDueAmountChange($event)"
                    @blur="onDueAmountChange($event)"
                  >
                  <em>₸</em>
                </div>
                <p v-if="catalogSubtotal > amount_of_order" class="pos__discount-line">
                  {{ $t('cash.payment.was') }} {{ formatReceiptMoney(catalogSubtotal) }} ₸
                </p>
                <p v-if="totalDiscountAmount > 0" class="pos__discount-line">
                  {{ $t('cash.discount.label') }} <b>−{{ totalDiscountAmount }} ₸</b>
                </p>
                <p v-if="manualDiscountAmount > 0" class="pos__discount-line">
                  {{ $t('cash.discount.receipt') }} <b>−{{ manualDiscountAmount }} ₸</b>
                </p>
                <p v-if="totalAmount() > amount_of_order" class="pos__change">
                  {{ $t('cash.payment.change') }} <b>{{ formatReceiptMoney(totalAmount() - amount_of_order) }}</b>
                </p>
              </div>

              <button type="button" class="pos__pay-btn pos__pay-btn--compact" :disabled="paying" @click="saveOrder">
                {{ paying ? $t('cash.payment.processing') : $t('cash.payment.pay') }}
              </button>
              </div>
            </div>
        </div>
      </aside>
              </div>

    <v-dialog width="460" v-model="show_loan" content-class="pos-loan-dialog">
      <div class="loan-modal">
        <header class="loan-modal__header">
          <div class="loan-modal__badge">
            <i class="mdi mdi-hand-coin-outline"></i>
              </div>
          <div>
            <h2>{{ $t('cash.loan.title') }}</h2>
            <p>{{ $t('cash.loan.savedAfterPayment') }}</p>
            </div>
          <button type="button" class="loan-modal__close" @click="show_loan = false">
            <i class="mdi mdi-close"></i>
          </button>
        </header>

        <div class="loan-modal__body">
          <label class="loan-modal__field">
            <span>{{ $t('cash.loan.debtorName') }}</span>
            <button
              v-if="touchMode"
              type="button"
              class="loan-modal__input-btn"
              @click="openLoanKeyboard('name')"
            >
              <span :class="{ 'is-placeholder': !name }">
                {{ name || $t('cash.loan.debtorNamePlaceholder') }}
              </span>
              <i class="mdi mdi-keyboard-outline"></i>
            </button>
            <input
                v-else
              v-model.trim="name"
              class="loan-modal__plain-input"
              type="text"
              :placeholder="$t('cash.loan.debtorNamePlaceholder')"
              maxlength="255"
            >
          </label>

          <div class="loan-modal__row">
            <label class="loan-modal__field">
              <span>{{ $t('cash.loan.amount') }}</span>
              <button
                v-if="touchMode"
                type="button"
                class="loan-modal__input-btn loan-modal__input-btn--amount"
                @click="openLoanKeyboard('amount')"
              >
                <span :class="{ 'is-placeholder': !loan }">{{ loan || '0' }}</span>
                <em>₸</em>
              </button>
              <div v-else class="loan-modal__input-wrap">
                <input
                  v-model="loan"
                  type="number"
                  min="0"
                  step="1"
                  inputmode="numeric"
                  placeholder="0"
                >
                <em>₸</em>
            </div>
            </label>

            <label class="loan-modal__field">
              <span>{{ $t('cash.loan.returnDate') }}</span>
              <button type="button" class="loan-modal__date" @click="open_date_picker = true">
                <i class="mdi mdi-calendar-month-outline"></i>
                <span>{{ date ? formatLoanDate(date) : $t('cash.loan.selectDate') }}</span>
              </button>
            </label>
          </div>

          <label class="loan-modal__field">
            <span>{{ $t('cash.loan.whatsappPhone') }}</span>
            <button
              v-if="touchMode"
              type="button"
              class="loan-modal__input-btn"
              @click="openLoanKeyboard('phone')"
            >
              <span :class="{ 'is-placeholder': !phone_loan }">
                {{ formatLoanPhoneInput(phone_loan) || $t('cash.loan.phonePlaceholder') }}
              </span>
              <i class="mdi mdi-keyboard-outline"></i>
            </button>
            <input
              v-else
              class="loan-modal__plain-input"
              :value="formatLoanPhoneInput(phone_loan)"
              type="tel"
              inputmode="numeric"
              data-touch-pad="phone"
              :placeholder="$t('cash.loan.phonePlaceholder')"
              maxlength="11"
              @input="onLoanPhoneTyping"
            >
          </label>

          <div class="loan-modal__phones">
            <div
              v-for="(extra, idx) in extra_phones_loan"
              :key="`loan-extra-${idx}`"
              class="loan-modal__phone-row"
            >
              <label class="loan-modal__field">
                <span>{{ $t('cash.loan.extraPhone') }} {{ idx + 1 }}</span>
                <button
                  v-if="touchMode"
                  type="button"
                  class="loan-modal__input-btn"
                  @click="openLoanKeyboard(`extra:${idx}`)"
                >
                  <span :class="{ 'is-placeholder': !extra }">
                    {{ formatLoanPhoneInput(extra) || $t('cash.loan.phonePlaceholder') }}
                  </span>
                  <i class="mdi mdi-keyboard-outline"></i>
                </button>
                <input
                  v-else
                  class="loan-modal__plain-input"
                  :value="formatLoanPhoneInput(extra)"
                  type="tel"
                  inputmode="numeric"
                  :placeholder="$t('cash.loan.phonePlaceholder')"
                  maxlength="11"
                  @input="onLoanExtraPhoneTyping(idx, $event)"
                >
              </label>
              <button
                type="button"
                class="loan-modal__phone-remove"
                :title="$t('cash.loan.removePhone')"
                @click="removeLoanExtraPhone(idx)"
              >
                <i class="mdi mdi-close"></i>
              </button>
            </div>
            <button
              v-if="extra_phones_loan.length < 5"
              type="button"
              class="loan-modal__phone-add"
              @click="addLoanExtraPhone"
            >
              <i class="mdi mdi-plus"></i>
              {{ $t('cash.loan.addPhone') }}
            </button>
          </div>

          <div v-if="loanDraftComplete" class="loan-modal__preview">
            <div>
              <span>{{ $t('cash.loan.totalDebt') }}</span>
              <strong>{{ formatLoanAmount(loan) }}</strong>
        </div>
        <div>
              <span>{{ $t('cash.loan.returnBy') }}</span>
              <strong>{{ formatLoanDate(date) }}</strong>
            </div>
            </div>
          </div>

        <footer class="loan-modal__footer">
          <button type="button" class="loan-modal__btn loan-modal__btn--ghost" @click="clearLoanAndClose">
            {{ $t('cash.actions.cancel') }}
          </button>
          <button type="button" class="loan-modal__btn" @click="closeLoanModal">
            {{ $t('cash.actions.done') }}
          </button>
        </footer>
        </div>
    </v-dialog>

    <v-dialog width="420" v-model="show_bonus">
      <v-card class="pos__dialog">
        <v-card-title>{{ $t('cash.bonus.redeem') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="amount_bonus" outlined dense :label="$t('cash.payment.amount')" />
          <p v-if="bonus_message" class="pos__bonus-miss">{{ bonus_message }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="show_bonus = false">{{ $t('cash.actions.cancel') }}</v-btn>
          <v-btn color="#0b4f8a" dark @click="subBonusPart">{{ $t('cash.bonus.redeem') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="bonusClientOpen" max-width="460" content-class="pos-bonus-client-dialog">
      <div class="bonus-client-modal">
        <header class="bonus-client-modal__header">
          <div class="bonus-client-modal__badge">
            <i class="mdi mdi-account-plus-outline"></i>
        </div>
          <div>
            <h2>{{ $t('cash.bonus.newClient') }}</h2>
            <p>{{ $t('cash.bonus.clientDetails') }}</p>
          </div>
          <button type="button" class="bonus-client-modal__close" @click="bonusClientOpen = false">
            <i class="mdi mdi-close"></i>
          </button>
        </header>

        <div class="bonus-client-modal__body">
          <label class="bonus-client-modal__field">
            <span>{{ $t('cash.bonus.fullName') }}</span>
            <input
              id="bonus-client-name"
              name="bonus_client_name"
              v-model.trim="bonusClientForm.name"
              type="text"
              :placeholder="$t('cash.bonus.fullNamePlaceholder')"
              maxlength="255"
            >
          </label>

          <label class="bonus-client-modal__field">
            <span>{{ $t('cash.bonus.phone') }}</span>
            <masked-input
              class="bonus-client-modal__phone"
              placeholder="8 (XXX) XXX-XX-XX"
              v-model.trim="bonusClientForm.phone"
                mask="8(111) 111-11-11"
                type="tel"
            />
          </label>

          <label class="bonus-client-modal__field">
            <span>{{ $t('cash.bonus.cardNumber') }}</span>
            <div class="bonus-client-modal__card">
              <input
                id="bonus-client-card"
                name="bonus_client_card"
                :value="bonusClientForm.card_number"
                type="text"
                readonly
                :placeholder="$t('cash.bonus.generating')"
              >
              <button
                type="button"
                class="bonus-client-modal__refresh"
                :title="$t('cash.bonus.generateAnother')"
                :disabled="bonusClientLoading"
                @click="loadBonusCardNumber"
              >
                <i class="mdi" :class="bonusClientLoading ? 'mdi-loading mdi-spin' : 'mdi-refresh'"></i>
              </button>
            </div>
          </label>
        </div>

        <footer class="bonus-client-modal__footer">
          <button type="button" class="bonus-client-modal__btn bonus-client-modal__btn--ghost" @click="bonusClientOpen = false">
            {{ $t('cash.actions.cancel') }}
          </button>
          <button
            type="button"
            class="bonus-client-modal__btn"
            :disabled="bonusClientSaving"
            @click="saveBonusClient"
          >
            {{ bonusClientSaving ? $t('cash.actions.saving') : $t('cash.actions.save') }}
          </button>
        </footer>
      </div>
    </v-dialog>

    <v-dialog v-model="receiptOpen" max-width="340" width="340" content-class="receipt-dialog">
      <div class="receipt-modal">
        <button
          type="button"
          class="receipt-modal__close"
          :title="$t('cash.receipt.close')"
          @click="receiptOpen = false"
        >
          <i class="mdi mdi-close"></i>
        </button>
        <div ref="receiptPrintable" class="receipt">
          <header class="receipt__header">
            <span class="receipt__mark">S</span>
            <strong>{{ receipt.company || companyName }}</strong>
            <p>{{ $t('cash.receipt.number', { number: receipt.orderId || '—' }) }}</p>
            <time>{{ formatReceiptDate(receipt.createdAt) }}</time>
          </header>

          <div class="receipt__dash"></div>

          <div v-if="receipt.isQuickSale" class="receipt__quick">
            <span>{{ $t('cash.receipt.quickSale') }}</span>
          </div>
          <div v-else class="receipt__items">
            <div v-for="(item, idx) in receipt.items" :key="`${item.id}-${idx}`" class="receipt__item">
              <strong>{{ item.name }}</strong>
              <span
                v-for="(mod, mIdx) in (item.modifiers || [])"
                :key="`${idx}-mod-${mIdx}`"
                class="receipt__mod"
              >
                + {{ mod.name }}
                <template v-if="mod.price"> ({{ formatReceiptMoney(mod.price) }})</template>
              </span>
              <span>{{ $t('cash.receipt.quantity', { count: item.quantity, price: formatReceiptMoney(item.unitPrice) }) }}</span>
              <b>{{ formatReceiptMoney(item.total) }}</b>
            </div>
          </div>

          <div class="receipt__dash"></div>

          <div class="receipt__payments">
            <div v-for="(payment, index) in receipt.payments" :key="`${payment.type}-${index}`">
              <span>{{ payment.label }}</span>
              <b>{{ formatReceiptMoney(payment.amount) }}</b>
            </div>
            </div>

          <div class="receipt__total">
            <span>{{ $t('cash.receipt.total') }}</span>
            <strong>{{ formatReceiptMoney(receipt.total) }}</strong>
          </div>

          <footer class="receipt__footer">
            <span>{{ $t('cash.receipt.cashier') }}: {{ receipt.cashier || '—' }}</span>
            <strong>{{ $t('cash.receipt.title') }}</strong>
          </footer>
        </div>

        <footer class="receipt-modal__actions">
          <label class="receipt-modal__auto">
            <input
              id="receipt-auto-print"
              name="auto_print"
              type="checkbox"
              :checked="autoPrintZywell"
              @change="toggleAutoPrint"
            >
            {{ $t('cash.receipt.autoPrint') }}
            <span v-if="printerOnline" class="receipt-modal__online">· online</span>
          </label>
          <button type="button" class="receipt-modal__btn receipt-modal__btn--ghost" @click="receiptOpen = false">
            {{ $t('cash.receipt.close') }}
          </button>
          <button type="button" class="receipt-modal__btn receipt-modal__btn--ghost" @click="saveReceiptImage">
            <i class="mdi mdi-image-outline"></i>
            {{ $t('cash.receipt.saveImage') }}
          </button>
          <button type="button" class="receipt-modal__btn" @click="printReceipt">
            <i class="mdi mdi-printer-outline"></i>
            {{ printerOnline ? $t('cash.receipt.printPrinter') : $t('cash.receipt.print') }}
          </button>
        </footer>
      </div>
    </v-dialog>

    <v-dialog width="320" v-model="open_date_picker" content-class="pos-loan-date">
      <div class="loan-date">
        <v-date-picker
          v-model="date"
          :locale="datePickerLocale"
          full-width
          color="#0b4f8a"
          no-title
        />
        <button type="button" class="loan-modal__btn" @click="open_date_picker = false">
          {{ $t('cash.actions.select') }}
        </button>
      </div>
    </v-dialog>

    <v-dialog v-model="cameraScannerOpen" max-width="460" content-class="pos-scanner-dialog">
      <section class="pos-scanner">
        <header class="pos-scanner__header">
          <div>
            <h2>{{ $t('cash.scanner.title') }}</h2>
            <p>{{ $t('cash.scanner.hint') }}</p>
              </div>
          <button type="button" @click="cameraScannerOpen = false">
            <i class="mdi mdi-close"></i>
          </button>
        </header>
        <div class="pos-scanner__camera">
          <StreamBarcodeReader
            @decode="onCameraBarcodeDecoded"
            @loaded="onCameraScannerLoaded"
            @error="onCameraScannerError"
          />
          <div class="pos-scanner__frame" aria-hidden="true">
            <i></i><i></i><i></i><i></i>
            <span></span>
            </div>
          <div v-if="cameraScannerLoading" class="pos-scanner__loading">
            <i class="mdi mdi-loading mdi-spin"></i>
            {{ $t('cash.scanner.connecting') }}
          </div>
          </div>
        <button type="button" class="pos-scanner__close" @click="cameraScannerOpen = false">
          {{ $t('cash.scanner.close') }}
        </button>
      </section>
    </v-dialog>

    <v-dialog
      v-model="qtyPickerOpen"
      max-width="320"
      width="320"
      content-class="pos-qty-dialog"
      persistent
    >
      <div v-if="qtyPickerProduct" class="qty-modal">
        <header class="qty-modal__header">
          <div>
            <p class="qty-modal__hint">{{ $t('cash.qtyModal.title') }}</p>
            <h2>{{ qtyPickerProduct.name }}</h2>
          </div>
          <button type="button" class="qty-modal__close" @click="closeQtyPicker">
            <i class="mdi mdi-close"></i>
          </button>
        </header>

        <div class="qty-modal__display">
          <strong>{{ qtyPickerDisplay }}</strong>
          <span>{{ formatReceiptMoney(qtyPickerUnitPrice) }}</span>
        </div>

        <button
          v-if="qtyPickerIsWeight"
          type="button"
          class="qty-modal__switch"
          :class="{
            'is-on': qtyPickerAutoWeight,
            'is-loading': qtyPickerScaleLoading,
          }"
          :disabled="qtyPickerScaleLoading"
          :aria-pressed="qtyPickerAutoWeight ? 'true' : 'false'"
          @click="toggleQtyAutoWeight"
        >
          <span>
            <i class="mdi mdi-scale-bathroom"></i>
            {{ $t('cash.qtyModal.autoWeight') }}
          </span>
          <i class="qty-modal__switch-track" aria-hidden="true">
            <em></em>
          </i>
        </button>

        <div class="qty-modal__pad">
          <button
            v-for="(key, keyIndex) in qtyPadKeys"
            :key="`qty-pad-${keyIndex}`"
            type="button"
            class="qty-modal__key"
            :class="{
              'qty-modal__key--action': key === 'back',
              'qty-modal__key--ghost': key === '',
            }"
            :disabled="key === ''"
            @click="pressQtyKey(key)"
          >
            <i v-if="key === 'back'" class="mdi mdi-backspace-outline"></i>
            <template v-else>{{ key }}</template>
          </button>
        </div>

        <div class="qty-modal__actions">
          <button type="button" class="qty-modal__cancel" @click="closeQtyPicker">
            {{ $t('cash.actions.cancel') }}
          </button>
          <button type="button" class="qty-modal__ok" @click="confirmQtyPicker">
            OK
          </button>
      </div>
    </div>
    </v-dialog>

    <v-dialog v-model="modifierPickerOpen" max-width="460" content-class="pos-modifier-dialog">
      <v-card v-if="modifierPickerProduct">
        <v-card-title>{{ $t('cash.modifiers.title') }}</v-card-title>
        <v-card-subtitle>
          {{ $t('cash.modifiers.subtitle', { name: modifierPickerProduct.name }) }}
        </v-card-subtitle>
        <v-card-text>
          <label
            v-for="mod in productModifiers(modifierPickerProduct)"
            :key="mod.id"
            class="pos-modifier-option"
          >
            <input
              :id="`pos-mod-${mod.id}`"
              :name="`modifier_${mod.id}`"
              v-model="modifierPickerSelected"
              type="checkbox"
              :value="mod.id"
            >
            <span>{{ mod.name }}</span>
            <strong>+{{ Number(mod.price) || 0 }} ₸</strong>
          </label>
          <p class="pos-modifier-total">
            {{ $t('cash.modifiers.selected', { amount: modifierPickerExtra }) }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="addWithoutModifiers">{{ $t('cash.modifiers.none') }}</v-btn>
          <v-spacer />
          <v-btn color="#0b4f8a" dark @click="confirmModifierPicker">{{ $t('cash.modifiers.add') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <SearchKeyboard
      v-if="searchKeyboardOpen"
      :open="true"
      :value="search"
      :locale="$i18n.locale"
      @input="onSearchKeyboardInput"
      @close="closeSearchKeyboard"
    />

    <SearchKeyboard
      v-if="touchMode && loanKeyboard === 'name'"
      :open="true"
      :value="name"
      :locale="$i18n.locale"
      @input="onLoanNameInput"
      @close="closeLoanKeyboard"
    />

    <NumericPad
      v-if="touchMode && loanKeyboard === 'amount'"
      :open="true"
      :value="loan"
      mode="number"
      :title="$t('cash.loan.amount')"
      placeholder="0"
      :max-length="10"
      @input="onLoanAmountInput"
      @close="closeLoanKeyboard"
    />

    <NumericPad
      v-if="touchMode && loanKeyboard === 'phone'"
      :open="true"
      :value="phone_loan"
      mode="phone"
      :title="$t('cash.loan.whatsappPhone')"
      :placeholder="$t('cash.loan.phonePlaceholder')"
      :max-length="11"
      @input="onLoanPhoneInput"
      @close="closeLoanKeyboard"
    />

    <NumericPad
      v-if="touchMode && loanExtraKeyboardIndex !== null"
      :open="true"
      :value="extra_phones_loan[loanExtraKeyboardIndex] || ''"
      mode="phone"
      :title="`${$t('cash.loan.extraPhone')} ${loanExtraKeyboardIndex + 1}`"
      :placeholder="$t('cash.loan.phonePlaceholder')"
      :max-length="11"
      @input="onLoanExtraPhonePadInput"
      @close="closeLoanKeyboard"
    />

    <NumericPad
      v-if="touchMode && paymentPadIndex !== null"
      :open="true"
      :value="paymentPadValue"
      mode="number"
      :title="paymentPadTitle"
      placeholder="0"
      :max-length="10"
      @input="onPaymentPadInput"
      @close="closePaymentPad"
    />

    <TouchNumberHost />
  </div>
</template>
<script>
import axios from 'axios'
import { debounce } from 'lodash'
import html2canvas from 'html2canvas'
import MaskedInput from 'vue-masked-input'
import { StreamBarcodeReader } from 'vue-barcode-reader'
import { getCategories } from '@/api/categories'
import { getDiscounts } from '@/api/discounts'
import { getProducts } from '@/api/products'
import { getPayments } from '@/api/sales'
import { peekLocalWeight, readScaleWeight } from '@/api/scale'
import { getCompanyName, setCompanyName } from '@/api/auth'
import { getPaperWidth, getPrinterHealth, isAutoPrintEnabled, setAutoPrintEnabled, setPaperWidth, tryPrintZywell } from '@/api/printer'
import http from '@/api/http'
import { notify, notifyError } from '@/utils/notify'
import { isTouchModeEnabled, setTouchModeEnabled } from '@/utils/touchMode'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import SearchKeyboard from '@/components/SearchKeyboard'
import NumericPad from '@/components/NumericPad'
import TouchNumberHost from '@/components/TouchNumberHost'

const STORAGE_KEY = 'kassa_sales'
const LOAN_DRAFT_PREFIX = 'sauda_loan_draft'
const AUTO_WEIGHT_KEY = 'sauda_auto_weight'
const PER_PAGE = 24

function isAutoWeightPrefEnabled() {
  return localStorage.getItem(AUTO_WEIGHT_KEY) !== '0'
}

function setAutoWeightPref(enabled) {
  localStorage.setItem(AUTO_WEIGHT_KEY, enabled ? '1' : '0')
}

const moneyFormatters = Object.create(null)

function getMoneyFormatter(locale) {
  if (!moneyFormatters[locale]) {
    moneyFormatters[locale] = new Intl.NumberFormat(locale)
  }
  return moneyFormatters[locale]
}

function loanDraftStorageKey() {
  const token = String(localStorage.getItem('access_token') || '')
  return `${LOAN_DRAFT_PREFIX}:${token.slice(-24) || 'current'}`
}

function normalizeSale(sale, index = 1) {
  const safePayments = Array.isArray(sale && sale.payments) && sale.payments.length
    ? sale.payments
    : [{ amount: 0, selected_payment: '' }]

  const slimOrders = (Array.isArray(sale && sale.orders) ? sale.orders : []).map((order) => {
    const modifiers = normalizeModifiers(order.modifiers)
    return {
      line_uid: order.line_uid || buildLineUid(order.id, modifiers),
      id: order.id,
      name: order.name,
      price: Number(order.price) || 0,
      quantity: Number(order.quantity) || 0,
      count_type: order.count_type || 'count',
      amount_value: Number(order.amount_value) || 0,
      count_value: Number(order.count_value) || 1,
      order_price: Number(order.order_price) || 0,
      discount_id: order.discount_id || null,
      product_discount: order.product_discount || null,
      byproducts_label: order.byproducts_label || null,
      modifiers,
      modifier_ids: modifiers.map((item) => item.id),
    }
  })

  return {
    id: (sale && sale.id) || `${Date.now()}-${index}`,
    title: (sale && sale.title) || '',
    orders: slimOrders,
    payments: safePayments.map((payment) => ({
      amount: Number(payment && payment.amount) || 0,
      selected_payment: (payment && payment.selected_payment) || '',
    })),
    give_bonus: !!(sale && sale.give_bonus),
    phone: (sale && sale.phone) || '',
    bonus:
      Array.isArray(sale && sale.bonus) || (sale && sale.bonus === 'Не найдено')
        ? sale.bonus
        : '',
    amount_of_order: Number(sale && sale.amount_of_order) || 0,
  }
}

function normalizeModifiers(list) {
  if (!Array.isArray(list)) return []
  return list
    .map((item) => ({
      id: Number(item && item.id) || 0,
      name: (item && item.name) || '',
      price: Number(item && item.price) || 0,
    }))
    .filter((item) => item.id > 0)
    .sort((a, b) => a.id - b.id)
}

function buildLineUid(productId, modifiers = []) {
  const ids = normalizeModifiers(modifiers).map((item) => item.id).join('-')
  return `${productId}:${ids || 'plain'}`
}

function createSale(titleIndex = 1) {
  return normalizeSale(
    {
      title: '',
      orders: [],
      payments: [{ amount: 0, selected_payment: '' }],
    },
    titleIndex
  )
}

function loadSales() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')
    if (saved && Array.isArray(saved.sales) && saved.sales.length) {
      const sales = saved.sales.map((sale, index) => normalizeSale(sale, index + 1))
      const matched = sales.find((sale) => sale.id === saved.activeSaleId)
      const activeSaleId = (matched && matched.id) || sales[0].id
      return { sales, activeSaleId }
    }

    const legacyOrders = JSON.parse(localStorage.getItem('orders') || '[]')
    const first = createSale(1)
    first.orders = Array.isArray(legacyOrders)
      ? legacyOrders.map((order) => ({
          id: order.id,
          name: order.name,
          price: Number(order.price) || 0,
          quantity: Number(order.quantity) || 0,
          count_type: order.count_type || 'count',
          amount_value: Number(order.amount_value) || 0,
          count_value: Number(order.count_value) || 1,
          order_price: Number(order.order_price) || 0,
        }))
      : []
    return { sales: [first], activeSaleId: first.id }
  } catch (e) {
    localStorage.removeItem(STORAGE_KEY)
    const first = createSale(1)
    return { sales: [first], activeSaleId: first.id }
  }
}

export default {
  name: 'CashInterface',
  components: {
    LanguageSwitcher,
    SearchKeyboard,
    NumericPad,
    TouchNumberHost,
    MaskedInput,
    StreamBarcodeReader,
  },
  data() {
    const initial = loadSales()
    return {
      show_bonus: false,
      amount_bonus: '',
      phone_loan: '',
      extra_phones_loan: [],
      bonusClientOpen: false,
      bonusClientLoading: false,
      bonusClientSaving: false,
      bonusClientForm: {
        name: '',
        phone: '',
        card_number: '',
      },
      bonusSearchTimer: null,
      open_date_picker: false,
      date: '',
      category_id: '',
      name: '',
      pageCount: 0,
      page: 1,
      categories: [],
      payment_types: [],
      discounts: [],
      modifierPickerOpen: false,
      modifierPickerProduct: null,
      modifierPickerSelected: [],
      qtyPickerOpen: false,
      qtyPickerProduct: null,
      qtyPickerModifiers: [],
      qtyPickerValue: '1',
      qtyPickerFresh: true,
      qtyPickerScaleLoading: false,
      qtyPickerAutoWeight: isAutoWeightPrefEnabled(),
      qtyPickerWeightReqId: 0,
      sales: initial.sales,
      activeSaleId: initial.activeSaleId,
      products: [],
      productCache: Object.create(null),
      productsLoading: false,
      productsRequestId: 0,
      productsCancelSource: null,
      ignorePageWatch: false,
      selected_payment: '',
      search: '',
      searchKeyboardOpen: false,
      loanKeyboard: null,
      paymentPadIndex: null,
      show_order: false,
      show_loan: false,
      loan: '',
      bonus_message: '',
      amount_pay: '',
      has_many_payment: false,
      saleCounter: initial.sales.length,
      paying: false,
      scaleLoadingId: null,
      saleMode: localStorage.getItem('sale_mode') || 'catalog',
      barcodeProcessing: false,
      cameraScannerOpen: false,
      cameraScannerLoading: true,
      cameraScannerProcessing: false,
      cameraScannerFallbackTimer: null,
      hardwareBarcodeBuffer: '',
      hardwareBarcodeLastKeyAt: 0,
      receiptOpen: false,
      autoPrintZywell: isAutoPrintEnabled(),
      touchMode: isTouchModeEnabled(),
      printerOnline: false,
      paperWidth: getPaperWidth(),
      scaleLive: null,
      scalePollTimer: null,
      scalePeekBusy: false,
      receipt: {
        orderId: null,
        createdAt: null,
        cashier: '',
        isQuickSale: false,
        items: [],
        payments: [],
        total: 0,
      },
    }
  },
  computed: {
    isQuickSaleMode() {
      return this.saleMode === 'quick'
    },
    modifierPickerExtra() {
      if (!this.modifierPickerProduct) return 0
      const selected = new Set((this.modifierPickerSelected || []).map(Number))
      return this.productModifiers(this.modifierPickerProduct)
        .filter((mod) => selected.has(Number(mod.id)))
        .reduce((sum, mod) => sum + (Number(mod.price) || 0), 0)
    },
    qtyPickerIsWeight() {
      const type = this.qtyPickerProduct && this.qtyPickerProduct.count_type
      return type === 'kg' || type === 'litr'
    },
    qtyPadKeys() {
      return this.qtyPickerIsWeight
        ? ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'back']
        : ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'back']
    },
    qtyPickerDisplay() {
      const raw = String(this.qtyPickerValue || '')
      return raw || '0'
    },
    qtyPickerUnitPrice() {
      if (!this.qtyPickerProduct) return 0
      return this.unitPrice({
        price: this.qtyPickerProduct.price,
        product_discount: this.qtyPickerProduct.product_discount_value
          ? {
            type: this.qtyPickerProduct.product_discount_type,
            value: Number(this.qtyPickerProduct.product_discount_value),
          }
          : this.qtyPickerProduct.product_discount || null,
        discount_id: null,
        modifiers: this.qtyPickerModifiers,
      })
    },
    activeSale() {
      if (!Array.isArray(this.sales) || !this.sales.length) {
        return createSale(1)
      }
      return this.sales.find((sale) => sale.id === this.activeSaleId) || this.sales[0]
    },
    orders: {
      get() {
        return (this.activeSale && this.activeSale.orders) || []
      },
      set(value) {
        if (this.activeSale) this.activeSale.orders = value
      },
    },
    payments: {
      get() {
        return (this.activeSale && this.activeSale.payments) || []
      },
      set(value) {
        if (this.activeSale) this.activeSale.payments = value
      },
    },
    give_bonus: {
      get() {
        return !!(this.activeSale && this.activeSale.give_bonus)
      },
      set(value) {
        if (this.activeSale) this.activeSale.give_bonus = value
      },
    },
    phone: {
      get() {
        return (this.activeSale && this.activeSale.phone) || ''
      },
      set(value) {
        if (this.activeSale) this.activeSale.phone = value
      },
    },
    bonus: {
      get() {
        return (this.activeSale && this.activeSale.bonus) || ''
      },
      set(value) {
        if (this.activeSale) this.activeSale.bonus = value
      },
    },
    bonusClient() {
      return Array.isArray(this.bonus) && this.bonus.length ? this.bonus[0] : null
    },
    bonusNotFound() {
      return this.bonus === '__not_found__' || this.bonus === 'Не найдено'
    },
    amount_of_order: {
      get() {
        return (this.activeSale && this.activeSale.amount_of_order) || 0
      },
      set(value) {
        if (this.activeSale) this.activeSale.amount_of_order = value
      },
    },
    paidTotal() {
      return this.payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0)
    },
    hasLoanPayment() {
      return this.payments.some((payment) => this.isLoanType(payment.selected_payment))
    },
    loanPaymentSelectedKey() {
      return this.payments
        .map((payment) => payment.selected_payment || '')
        .filter(Boolean)
        .sort()
        .join('|')
    },
    loanDraftComplete() {
      return !!(
        this.name &&
        this.loan &&
        Number(this.loan) > 0 &&
        this.date &&
        this.phone_loan &&
        this.phone_loan.replace(/\D/g, '').length === 11
      )
    },
    loanExtraKeyboardIndex() {
      const key = String(this.loanKeyboard || '')
      if (!key.startsWith('extra:')) return null
      const index = Number(key.slice(6))
      return Number.isInteger(index) && index >= 0 ? index : null
    },
    catalogSubtotal() {
      if (this.isQuickSaleMode) return Number(this.amount_of_order) || 0
      const total = this.orders.reduce((sum, order) => {
        const catalog = Number(order.catalog_order_price)
        if (Number.isFinite(catalog) && catalog > 0) return sum + catalog
        return sum + (Number(this.unitPrice(order)) || 0) * (Number(this.orderQty(order)) || 0)
      }, 0)
      return Math.round(total * 100) / 100
    },
    manualDiscountAmount() {
      if (this.isQuickSaleMode) return 0
      return Math.max(0, Math.round((this.catalogSubtotal - (Number(this.amount_of_order) || 0)) * 100) / 100)
    },
    totalDiscountAmount() {
      const line = this.orders.reduce(
        (sum, order) => sum + (Number(this.lineDiscount(order)) || 0),
        0
      )
      return Math.round((line + this.manualDiscountAmount) * 100) / 100
    },
    cashierName() {
      return localStorage.getItem('user_name') || ''
    },
    companyName() {
      return getCompanyName()
    },
    canOpenAdmin() {
      return (
        localStorage.getItem('company_role') !== 'cashier' ||
        localStorage.getItem('can_manage_products') === '1'
      )
    },
    datePickerLocale() {
      return this.$i18n.locale === 'kk' ? 'kk-KZ' : 'ru-RU'
    },
    scaleLiveText() {
      if (!this.scaleLive) return this.$t('cash.scale.offline')
      const value = Number(this.scaleLive.value) || 0
      if (value <= 0) return this.$t('cash.scale.empty')
      const unit = this.scaleLive.unit || 'kg'
      const num = value.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')
      const stability = this.scaleLive.stable
        ? this.$t('cash.scale.stable')
        : this.$t('cash.scale.unstable')
      return `${num} ${unit} · ${stability}`
    },
    paymentPadValue() {
      if (this.paymentPadIndex === null) return ''
      if (this.paymentPadIndex === 'due') {
        return this.amount_of_order ? String(Math.round(Number(this.amount_of_order) || 0)) : ''
      }
      const payment = this.payments[this.paymentPadIndex]
      const amount = payment && payment.amount
      return amount ? String(Math.round(Number(amount) || 0)) : ''
    },
    paymentPadTitle() {
      if (this.paymentPadIndex === null) return this.$t('cash.payment.amount')
      if (this.paymentPadIndex === 'due') return this.$t('cash.payment.due')
      const payment = this.payments[this.paymentPadIndex]
      return this.paymentTypeLabel(payment && payment.selected_payment) || this.$t('cash.payment.amount')
    },
  },
  created() {
    if (!Array.isArray(this.sales) || !this.sales.length) {
      const first = createSale(1)
      this.sales = [first]
      this.activeSaleId = first.id
    }
    this.restoreLoanDraft()
  },
  mounted() {
    if (!localStorage.getItem('access_token')) return
    this.loadSaleMode()
    this.loadPayments()
    this.refreshPrinterStatus()
    if (!this.isQuickSaleMode) {
      this.loadCategories()
      this.loadDiscounts()
      this.focusProductSearch()
    }
    window.addEventListener('keydown', this.handleHardwareBarcodeKeydown)
  },
  beforeDestroy() {
    this.cancelProductsRequest()
    if (this.schedulePersist.cancel) this.schedulePersist.cancel()
    if (this.schedulePersistLoanDraft && this.schedulePersistLoanDraft.cancel) {
      this.schedulePersistLoanDraft.cancel()
    }
    if (this.scheduleCartTotal && this.scheduleCartTotal.cancel) {
      this.scheduleCartTotal.cancel()
    }
    if (this.debouncedSearch.cancel) this.debouncedSearch.cancel()
    if (this.cameraScannerFallbackTimer) window.clearTimeout(this.cameraScannerFallbackTimer)
    if (this.bonusSearchTimer) window.clearTimeout(this.bonusSearchTimer)
    window.removeEventListener('keydown', this.handleHardwareBarcodeKeydown)
    this.stopScalePolling()
  },
  methods: {
    handleHardwareBarcodeKeydown(event) {
      if (this.isQuickSaleMode || event.ctrlKey || event.altKey || event.metaKey) return

      const target = event.target
      const isTypingField = target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
      if (isTypingField) return

      const now = Date.now()
      if (event.key === 'Enter') {
        if (this.hardwareBarcodeBuffer && now - this.hardwareBarcodeLastKeyAt < 150) {
          event.preventDefault()
          this.addProductByBarcode(this.hardwareBarcodeBuffer)
        }
        this.hardwareBarcodeBuffer = ''
        return
      }

      if (event.key.length !== 1) return
      this.hardwareBarcodeBuffer = now - this.hardwareBarcodeLastKeyAt > 100
        ? event.key
        : `${this.hardwareBarcodeBuffer}${event.key}`
      this.hardwareBarcodeLastKeyAt = now
    },
    scanFromSearch() {
      this.addProductByBarcode(this.search, true)
    },
    openCameraScanner() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        notify(this.$t('cash.notifications.cameraNotSupported'), 'warning')
        return
      }
      this.cameraScannerLoading = true
      this.cameraScannerProcessing = false
      this.cameraScannerOpen = true
      if (this.cameraScannerFallbackTimer) {
        window.clearTimeout(this.cameraScannerFallbackTimer)
      }
      // iOS Safari иногда не отправляет loaded, хотя камера уже активна.
      this.cameraScannerFallbackTimer = window.setTimeout(() => {
        if (this.cameraScannerOpen) this.cameraScannerLoading = false
      }, 1800)
    },
    onCameraScannerLoaded() {
      this.cameraScannerLoading = false
    },
    onCameraScannerError() {
      this.cameraScannerLoading = false
      notify(this.$t('cash.notifications.cameraNotSupported'), 'warning')
    },
    async onCameraBarcodeDecoded(result) {
      if (this.cameraScannerProcessing) return

      const barcode = typeof result === 'string'
        ? result
        : result && result.codeResult && result.codeResult.code
      if (!barcode) return

      this.cameraScannerProcessing = true
      const added = await this.addProductByBarcode(barcode)
      if (added) {
        this.search = ''
        this.cameraScannerOpen = false
      }
      this.cameraScannerProcessing = false
    },
    async addProductByBarcode(rawBarcode, silent = false) {
      const barcode = String(rawBarcode || '').trim()
      if (!barcode || this.barcodeProcessing || this.isQuickSaleMode) return false

      this.barcodeProcessing = true
      try {
        const data = await getProducts({ search: barcode, per_page: 10 })
        const product = (data.data || []).find((item) => String(item.barcode || '').trim() === barcode)

        if (!product) {
          if (!silent) notify(this.$t('cash.notifications.barcodeNotFound'), 'warning')
        return false
      }

        this.addOrder(product)
        this.search = ''
        return true
      } catch (error) {
        if (!silent) notifyError(error)
        return false
      } finally {
        this.barcodeProcessing = false
        this.focusProductSearch()
      }
    },
    focusProductSearch() {
      this.$nextTick(() => {
        if (this.$refs.productSearch) this.$refs.productSearch.focus()
      })
    },
    openSearchKeyboard() {
      this.searchKeyboardOpen = true
    },
    closeSearchKeyboard() {
      this.searchKeyboardOpen = false
    },
    onSearchKeyboardInput(value) {
      this.search = value
    },
    toggleTouchMode() {
      this.touchMode = setTouchModeEnabled(!this.touchMode)
      if (!this.touchMode) {
        this.closePaymentPad()
        this.closeLoanKeyboard()
      }
    },
    openLoanKeyboard(field) {
      if (!this.touchMode) return
      this.loanKeyboard = field
    },
    closeLoanKeyboard() {
      this.loanKeyboard = null
    },
    onLoanNameInput(value) {
      this.name = value
    },
    onLoanAmountInput(value) {
      this.loan = value
    },
    onLoanPhoneInput(value) {
      this.phone_loan = String(value || '').replace(/\D/g, '').slice(0, 11)
    },
    onLoanPhoneTyping(event) {
      this.onLoanPhoneInput(event && event.target && event.target.value)
    },
    addLoanExtraPhone() {
      if (this.extra_phones_loan.length >= 5) return
      this.extra_phones_loan.push('')
      this.schedulePersistLoanDraft()
    },
    removeLoanExtraPhone(index) {
      this.extra_phones_loan.splice(index, 1)
      if (this.loanExtraKeyboardIndex === index) {
        this.closeLoanKeyboard()
      }
      this.schedulePersistLoanDraft()
    },
    onLoanExtraPhoneTyping(index, event) {
      const digits = String((event && event.target && event.target.value) || '')
        .replace(/\D/g, '')
        .slice(0, 11)
      this.$set(this.extra_phones_loan, index, digits)
      this.schedulePersistLoanDraft()
    },
    onLoanExtraPhonePadInput(value) {
      const index = this.loanExtraKeyboardIndex
      if (index == null) return
      this.$set(
        this.extra_phones_loan,
        index,
        String(value || '').replace(/\D/g, '').slice(0, 11)
      )
      this.schedulePersistLoanDraft()
    },
    normalizedLoanExtraPhones() {
      const primary = String(this.phone_loan || '').replace(/\D/g, '')
      const seen = { [primary]: true }
      return (this.extra_phones_loan || [])
        .map((item) => String(item || '').replace(/\D/g, ''))
        .filter((digits) => {
          if (!digits || digits.length !== 11 || seen[digits]) return false
          seen[digits] = true
          return true
        })
    },
    formatLoanPhoneInput(value) {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 11)
      if (!digits) return ''
      let out = digits[0] || ''
      if (digits.length > 1) out += ` (${digits.slice(1, 4)}`
      if (digits.length >= 4) out += ')'
      if (digits.length > 4) out += ` ${digits.slice(4, 7)}`
      if (digits.length > 7) out += `-${digits.slice(7, 9)}`
      if (digits.length > 9) out += `-${digits.slice(9, 11)}`
      return out
    },
    createReceipt(data) {
      const paymentLabel = (value) => {
        const type = this.payment_types.find((item) => item.value === value)
        return (type && (type.name || type.title || type.label)) || value || '—'
      }

      this.receipt = {
        orderId: data.order_id || (data.data && data.data.id) || null,
        createdAt: new Date().toISOString(),
        createdAtLabel: this.formatReceiptDate(new Date().toISOString()),
        company: getCompanyName(),
        cashier: this.cashierName,
        isQuickSale: this.isQuickSaleMode,
        items: this.orders.map((order) => {
          const quantity = order.count_type === 'count'
            ? Number(order.count_value) || 1
            : Number(order.amount_value) || 0
          return {
            id: order.id,
            name: order.name,
            quantity,
            unitPrice: this.unitPrice(order),
            total: Number(order.order_price) || 0,
            modifiers: Array.isArray(order.modifiers)
              ? order.modifiers.map((mod) => ({
                id: mod.id,
                name: mod.name,
                price: Number(mod.price) || 0,
              }))
              : [],
          }
        }),
        payments: this.payments.map((payment) => ({
          type: payment.selected_payment,
          label: paymentLabel(payment.selected_payment),
          amount: Number(payment.amount) || 0,
        })),
        total: Number(this.amount_of_order) || 0,
      }
      this.receiptOpen = true
      this.refreshPrinterStatus()
      if (this.autoPrintZywell) {
        this.$nextTick(() => {
          tryPrintZywell(this.receipt, { silent: false })
        })
      }
    },
    toggleAutoPrint(event) {
      this.autoPrintZywell = !!(event && event.target && event.target.checked)
      setAutoPrintEnabled(this.autoPrintZywell)
    },
    onPaperWidthChange() {
      this.paperWidth = setPaperWidth(this.paperWidth)
    },
    async refreshPrinterStatus() {
      const health = await getPrinterHealth()
      this.printerOnline = !!(health && health.ok)
    },
    formatReceiptMoney(value) {
      return `${getMoneyFormatter(this.datePickerLocale).format(Number(value) || 0)} ₸`
    },
    formatReceiptDate(value) {
      if (!value) return '—'
      return new Intl.DateTimeFormat(this.datePickerLocale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    },
    async saveReceiptImage() {
      const source = this.$refs.receiptPrintable
      if (!source) return

      const canvas = await html2canvas(source, {
        backgroundColor: '#ffffff',
        scale: Math.min(window.devicePixelRatio || 1, 2),
      })
      const link = document.createElement('a')
      link.download = `sauda-cheque-${this.receipt.orderId || Date.now()}.png`
      link.href = canvas.toDataURL('image/png')
      link.click()
    },
    async printReceipt() {
      const printed = await tryPrintZywell(this.receipt, { silent: true })
      if (printed) {
        notify('Чек отправлен на принтер')
        return
      }

      const source = this.$refs.receiptPrintable
      if (!source) return

      const printWindow = window.open('', '_blank', 'width=420,height=680')
      if (!printWindow) {
        notify('Разрешите всплывающие окна для печати чека', 'warning')
        return
      }

      printWindow.document.write(`<!doctype html><html><head><title>${getCompanyName() || 'Чек'}</title><style>
        body{margin:0;padding:16px;background:#fff;font-family:Arial,sans-serif;color:#152033}
        .receipt{max-width:340px;margin:0 auto;font-size:13px}
        .receipt__header{text-align:center}.receipt__mark{display:inline-grid;place-items:center;width:28px;height:28px;margin-right:6px;border-radius:8px;background:#0b4f8a;color:#fff;font-weight:800}.receipt__header p,.receipt__header time{display:block;margin:6px 0 0;color:#64748b;font-size:12px}
        .receipt__dash{margin:14px 0;border-top:1px dashed #94a3b8}.receipt__item{display:grid;grid-template-columns:1fr auto;gap:3px;margin:9px 0}.receipt__item strong{grid-column:1/-1}.receipt__item span{color:#64748b}.receipt__item b,.receipt__payments b{font-weight:700}
        .receipt__mod{grid-column:1/-1;font-size:12px;color:#64748b}.receipt__payments div{display:flex;justify-content:space-between;margin:7px 0}.receipt__total{display:flex;justify-content:space-between;font-size:18px;font-weight:800}.receipt__footer{display:grid;gap:10px;margin-top:18px;text-align:center;color:#64748b;font-size:12px}.receipt__footer strong{color:#0b4f8a}
      </style></head><body>${source.outerHTML}</body></html>`)
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()
    },
    saleTitle(sale, index) {
      return sale.title || this.$t('cash.sales.receipt', { number: index + 1 })
    },
    logout() {
      localStorage.removeItem('access_token')
      localStorage.removeItem('company_role')
      localStorage.removeItem('can_manage_products')
      localStorage.removeItem('user_name')
      localStorage.removeItem('sale_mode')
      localStorage.removeItem('company_name')
      this.$router.push('/kassa-login')
    },
    async loadSaleMode() {
      try {
        const { data } = await http.get('me')
        if (data.company_name) setCompanyName(data.company_name)
        const mode = data.sale_mode === 'quick' ? 'quick' : 'catalog'
        this.saleMode = mode
        localStorage.setItem('sale_mode', mode)
        if (mode === 'catalog') {
          if (!this.categories.length) this.loadCategories()
          if (!this.discounts.length) this.loadDiscounts()
          this.focusProductSearch()
        }
      } catch (error) {
        // оставляем значение из localStorage
      }
    },
    onQuickAmountInput(event) {
      const value = Math.max(0, Number(event.target.value) || 0)
      this.amount_of_order = value
      if (this.payments[0]) {
        this.payments[0].amount = value
      }
      if (this.hasLoanPayment) {
        this.loan = value
      }
    },
    scrollToCart() {
      const cart = this.$refs.cartSection
      if (cart && cart.scrollIntoView) {
        cart.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    isLoanType(value) {
      return value === 'qaryz' || value === 'loan'
    },
    formatLoanAmount(value) {
      const amount = Number(value) || 0
      return `${getMoneyFormatter(this.datePickerLocale).format(amount)} ₸`
    },
    formatLoanPhone(phone) {
      const digits = String(phone || '').replace(/\D/g, '')
      if (digits.length !== 11) return phone || '—'
      return `${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`
    },
    formatLoanDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString(this.datePickerLocale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    },
    prepareLoanDraft({ openModal = false } = {}) {
      const loanPayment = this.payments.find((payment) => this.isLoanType(payment.selected_payment))
      if (!this.loan) {
        this.loan = loanPayment ? loanPayment.amount : this.amount_of_order
      }
      if (openModal && !this.loanDraftComplete) {
        this.show_loan = true
      }
    },
    syncLoanPaymentSelection(hasLoan) {
      if (hasLoan) {
        this.prepareLoanDraft({ openModal: false })
        this.schedulePersistLoanDraft()
        return
      }
      if (this.name || this.loan || this.phone_loan || this.extra_phones_loan.length || this.date) {
        this.name = ''
        this.loan = ''
        this.phone_loan = ''
        this.extra_phones_loan = []
        this.date = ''
        this.closeLoanKeyboard()
        localStorage.removeItem(loanDraftStorageKey())
      }
    },
    closeLoanModal() {
      if (!this.name) {
        notify(this.$t('cash.notifications.enterDebtorName'), 'warning')
        return
      }
      if (!this.phone_loan || this.phone_loan.replace(/\D/g, '').length !== 11) {
        notify(this.$t('cash.notifications.enterDebtorPhone'), 'warning')
        return
      }
      const incompleteExtra = (this.extra_phones_loan || []).some((item) => {
        const digits = String(item || '').replace(/\D/g, '')
        return digits.length > 0 && digits.length !== 11
      })
      if (incompleteExtra) {
        notify(this.$t('cash.notifications.enterDebtorPhone'), 'warning')
        return
      }
      if (!this.loan || Number(this.loan) <= 0) {
        notify(this.$t('cash.notifications.enterDebtAmount'), 'warning')
        return
      }
      if (!this.date) {
        notify(this.$t('cash.notifications.enterReturnDate'), 'warning')
        return
      }
      this.show_loan = false
      this.closeLoanKeyboard()
    },
    clearLoanAndClose() {
      this.payments.forEach((payment) => {
        if (this.isLoanType(payment.selected_payment)) {
          payment.selected_payment = ''
        }
      })
      this.clearLoanDraft()
    },
    clearLoanDraft() {
      this.name = ''
      this.loan = ''
      this.phone_loan = ''
      this.extra_phones_loan = []
      this.date = ''
      this.show_loan = false
      this.closeLoanKeyboard()
      localStorage.removeItem(loanDraftStorageKey())
    },
    restoreLoanDraft() {
      if (!this.hasLoanPayment) return

      try {
        const draft = JSON.parse(localStorage.getItem(loanDraftStorageKey()) || 'null')
        if (!draft || typeof draft !== 'object') return

        this.name = draft.name || ''
        this.loan = draft.loan || ''
        this.phone_loan = draft.phone_loan || ''
        this.extra_phones_loan = Array.isArray(draft.extra_phones_loan)
          ? draft.extra_phones_loan.map((item) => String(item || '').replace(/\D/g, '').slice(0, 11))
          : []
        this.date = draft.date || ''
      } catch (error) {
        localStorage.removeItem(loanDraftStorageKey())
      }
    },
    persistLoanDraft() {
      if (!this.hasLoanPayment) return

      localStorage.setItem(loanDraftStorageKey(), JSON.stringify({
        name: this.name,
        loan: this.loan,
        phone_loan: this.phone_loan,
        extra_phones_loan: this.extra_phones_loan,
        date: this.date,
        saved_at: new Date().toISOString(),
      }))
    },
    schedulePersistLoanDraft: debounce(function () {
      this.persistLoanDraft()
    }, 400),
    cacheKey(categoryId, page) {
      return `${categoryId}:${page}`
    },
    setPageQuiet(page) {
      if (this.page === page) return
      this.ignorePageWatch = true
      this.page = page
      this.$nextTick(() => {
        this.ignorePageWatch = false
      })
    },
    cancelProductsRequest() {
      if (this.productsCancelSource) {
        this.productsCancelSource.cancel('cancelled')
        this.productsCancelSource = null
      }
    },
    applyProducts(list, pageCount) {
      this.products = list
      this.pageCount = pageCount
    },
    async fetchProducts({ useCache = true } = {}) {
      const categoryId = this.category_id
      const page = this.page
      const search = (this.search || '').trim()

      if (!search && !categoryId) {
        this.applyProducts([], 0)
        this.productsLoading = false
        return
      }

      const key = !search && categoryId ? this.cacheKey(categoryId, page) : null
      if (useCache && key && this.productCache[key]) {
        const cached = this.productCache[key]
        this.applyProducts(cached.products, cached.pageCount)
      }

      this.cancelProductsRequest()
      const requestId = ++this.productsRequestId
      const source = axios.CancelToken.source()
      this.productsCancelSource = source
      this.productsLoading = true

      try {
        const params = {
          page,
          per_page: PER_PAGE,
        }
        if (search) {
          params.search = search
        } else {
          params.category_id = categoryId
        }

        const data = await getProducts(params, { cancelToken: source.token })
        if (requestId !== this.productsRequestId) return

        const products = data.data || []
        const pageCount = Number(data.last_page) || 0
        this.applyProducts(products, pageCount)

        if (key) {
          this.$set(this.productCache, key, { products, pageCount })
        }
      } catch (error) {
        if (axios.isCancel(error)) return
        if (requestId !== this.productsRequestId) return
        notifyError(error, this.$t('cash.notifications.productsLoadFailed'))
      } finally {
        if (requestId === this.productsRequestId) {
          this.productsLoading = false
          this.productsCancelSource = null
        }
      }
    },
    selectCategory(id) {
      if (this.category_id === id && !this.search) return

      const hadSearch = !!(this.search && this.search.trim())
      this.category_id = id
      this.setPageQuiet(1)

      if (hadSearch) {
        // clearing search triggers watcher which loads products
        this.search = ''
        return
      }

      this.fetchProducts({ useCache: true })
    },
    persistSales() {
      try {
        const sales = this.sales.map((sale) => ({
          id: sale.id,
          title: sale.title,
          orders: sale.orders,
          payments: (sale.payments || []).map((payment) => ({
            amount: Number(payment.amount) || 0,
            selected_payment: payment.selected_payment || '',
          })),
          give_bonus: !!sale.give_bonus,
          phone: sale.phone || '',
          bonus: sale.bonus || '',
          amount_of_order: Number(sale.amount_of_order) || 0,
        }))

        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            sales,
            activeSaleId: this.activeSaleId,
          })
        )
      } catch (e) {
        // ignore quota errors
      }
    },
    schedulePersist: debounce(function () {
      this.persistSales()
    }, 600),
    debouncedSearch: debounce(function () {
      this.setPageQuiet(1)
      this.fetchProducts({ useCache: false })
    }, 350),
    async loadCategories() {
      try {
        this.categories = await getCategories()
        if (this.categories.length && !this.category_id) {
          this.selectCategory(this.categories[0].id)
        }
      } catch (error) {
        notifyError(error)
      }
    },
    async loadPayments() {
      try {
        const types = await getPayments()
        this.payment_types = Array.isArray(types) ? types : []
      } catch (error) {
        this.payment_types = []
        notifyError(error, this.$t('cash.notifications.paymentTypesLoadFailed'))
      }
    },
    async loadDiscounts() {
      try {
        const discounts = await getDiscounts({ active_only: 1 })
        this.discounts = Array.isArray(discounts) ? discounts : []
      } catch (error) {
        this.discounts = []
      }
    },
    orderDiscount(order) {
      if (order.discount_id) {
        return this.discounts.find((d) => d.id === Number(order.discount_id)) || null
      }
      return order.product_discount || null
    },
    discountShort(discount) {
      return discount.type === 'percent'
        ? `−${Number(discount.value)}%`
        : `−${Number(discount.value)} ₸`
    },
    unitPrice(order) {
      const base = Number(order.price) || 0
      const discount = this.orderDiscount(order)
      let productUnit = base
      if (discount) {
        const cut = discount.type === 'percent'
          ? (base * Number(discount.value)) / 100
          : Math.min(Number(discount.value), base)
        productUnit = Math.max(0, Math.round((base - cut) * 100) / 100)
      }
      const mods = Array.isArray(order.modifiers) ? order.modifiers : []
      const modifiersTotal = mods.reduce((sum, mod) => sum + (Number(mod.price) || 0), 0)
      return Math.round((productUnit + modifiersTotal) * 100) / 100
    },
    orderQty(order) {
      return order.count_type === 'count'
        ? Number(order.count_value) || 1
        : Number(order.amount_value) || 0
    },
    productUnitBase(order) {
      const base = Number(order.price) || 0
      const mods = Array.isArray(order.modifiers) ? order.modifiers : []
      const modifiersTotal = mods.reduce((sum, mod) => sum + (Number(mod.price) || 0), 0)
      return Math.round((base + modifiersTotal) * 100) / 100
    },
    lineDiscount(order) {
      const base = Number(order.price) || 0
      const discount = this.orderDiscount(order)
      if (!discount) return 0
      const cut = discount.type === 'percent'
        ? (base * Number(discount.value)) / 100
        : Math.min(Number(discount.value), base)
      return Math.round(Math.max(0, cut) * this.orderQty(order) * 100) / 100
    },
    productModifiers(product) {
      return normalizeModifiers((product && product.modifiers) || [])
    },
    productByproducts(product) {
      const list = product && product.byproducts
      return Array.isArray(list) ? list : []
    },
    productByproductsQty(product) {
      return this.productByproducts(product).reduce((sum, row) => {
        const qty = Number(row && row.quantity)
        return sum + (Number.isFinite(qty) && qty > 0 ? qty : 0)
      }, 0)
    },
    productAvailableQty(product) {
      const main = Number(product && product.quantity)
      const mainSafe = Number.isFinite(main) && main > 0 ? main : 0
      return Math.round((mainSafe + this.productByproductsQty(product)) * 1000) / 1000
    },
    productByproductsLabel(product) {
      return this.productByproducts(product)
        .filter((row) => {
          const qty = Number(row && row.quantity)
          return Number.isFinite(qty) ? qty > 0 : !!row
        })
        .map((row) => {
          const qty = Number(row.quantity)
          const unit = row.count_type === 'kg'
            ? this.$t('cash.cart.unitKg')
            : row.count_type === 'litr'
              ? this.$t('cash.cart.unitLiter')
              : this.$t('cash.cart.unitCount')
          const qtyLabel = Number.isFinite(qty) ? qty : row.quantity
          return `${row.name} ${qtyLabel} ${unit}`.trim()
        })
        .join(' · ')
    },
    closeModifierPicker() {
      this.modifierPickerOpen = false
      this.modifierPickerProduct = null
      this.modifierPickerSelected = []
    },
    addWithoutModifiers() {
      const product = this.modifierPickerProduct
      this.closeModifierPicker()
      if (product) this.openQtyPicker(product, [])
    },
    confirmModifierPicker() {
      const product = this.modifierPickerProduct
      if (!product) return
      const selected = new Set((this.modifierPickerSelected || []).map(Number))
      const modifiers = this.productModifiers(product).filter((mod) => selected.has(Number(mod.id)))
      this.closeModifierPicker()
      this.openQtyPicker(product, modifiers)
    },
    openQtyPicker(product, modifiers = []) {
      if (!product) return
      const available = this.productAvailableQty(product)
      if (available <= 0) {
        notify(this.$t('cash.notifications.noStockRestock'), 'warning')
        return
      }
      const isWeight = product.count_type === 'kg' || product.count_type === 'litr'
      this.qtyPickerWeightReqId += 1
      this.qtyPickerProduct = product
      this.qtyPickerModifiers = normalizeModifiers(modifiers)
      this.qtyPickerValue = isWeight ? '' : '1'
      this.qtyPickerFresh = true
      this.qtyPickerScaleLoading = false
      this.qtyPickerAutoWeight = isWeight ? isAutoWeightPrefEnabled() : false
      this.qtyPickerOpen = true
      if (isWeight && this.qtyPickerAutoWeight) {
        this.$nextTick(() => {
          this.readWeightForQtyPicker({ silent: true, retries: 5 })
        })
      }
    },
    closeQtyPicker() {
      this.qtyPickerWeightReqId += 1
      this.qtyPickerOpen = false
      this.qtyPickerProduct = null
      this.qtyPickerModifiers = []
      this.qtyPickerValue = '1'
      this.qtyPickerFresh = true
      this.qtyPickerScaleLoading = false
    },
    toggleQtyAutoWeight() {
      if (!this.qtyPickerIsWeight || this.qtyPickerScaleLoading) return
      const enabled = !this.qtyPickerAutoWeight
      this.qtyPickerAutoWeight = enabled
      setAutoWeightPref(enabled)
      if (enabled) {
        this.readWeightForQtyPicker({ silent: false, retries: 5 })
      }
    },
    pressQtyKey(key) {
      if (key === '' || key == null) return
      if (this.qtyPickerAutoWeight && this.qtyPickerIsWeight && key !== 'back') {
        this.qtyPickerAutoWeight = false
      }
      if (key === 'back') {
        if (this.qtyPickerFresh || !this.qtyPickerValue || this.qtyPickerValue.length <= 1) {
          this.qtyPickerValue = ''
          this.qtyPickerFresh = false
          return
        }
        this.qtyPickerValue = String(this.qtyPickerValue).slice(0, -1)
        return
      }
      if (key === '.') {
        if (!this.qtyPickerIsWeight) return
        if (this.qtyPickerFresh || !this.qtyPickerValue) {
          this.qtyPickerValue = '0.'
          this.qtyPickerFresh = false
          return
        }
        if (String(this.qtyPickerValue).includes('.')) return
        this.qtyPickerValue = `${this.qtyPickerValue}.`
        return
      }
      if (this.qtyPickerFresh) {
        this.qtyPickerValue = String(key)
        this.qtyPickerFresh = false
        return
      }
      const next = `${this.qtyPickerValue || ''}${key}`
      if (this.qtyPickerIsWeight) {
        if (!/^\d*\.?\d{0,3}$/.test(next)) return
        this.qtyPickerValue = next
        return
      }
      if (!/^\d{1,6}$/.test(next)) return
      this.qtyPickerValue = next
    },
    async readWeightForQtyPicker({ silent = false, retries = 1 } = {}) {
      if (!this.qtyPickerOpen || !this.qtyPickerIsWeight || !this.qtyPickerAutoWeight) return
      const reqId = this.qtyPickerWeightReqId + 1
      this.qtyPickerWeightReqId = reqId
      this.qtyPickerScaleLoading = true
      const maxAttempts = Math.max(1, Number(retries) || 1)
      let lastError = null
      let gotZero = false

      try {
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          if (
            this.qtyPickerWeightReqId !== reqId
            || !this.qtyPickerOpen
            || !this.qtyPickerAutoWeight
          ) {
            return
          }

          try {
            let weight = await peekLocalWeight()
            if (!weight || !(Number(weight.value) > 0)) {
              weight = await readScaleWeight()
            }
            if (
              this.qtyPickerWeightReqId !== reqId
              || !this.qtyPickerOpen
              || !this.qtyPickerAutoWeight
            ) {
              return
            }
            const value = Number(weight && weight.value)
            if (!value || value <= 0) {
              gotZero = true
              if (attempt < maxAttempts - 1) {
                await new Promise((resolve) => setTimeout(resolve, 350))
                continue
              }
              break
            }
            this.qtyPickerValue = String(value)
            this.qtyPickerFresh = true
            this.scaleLive = {
              value,
              unit: (weight && weight.unit) || 'kg',
              stable: !!(weight && weight.stable),
              source: (weight && weight.source) || 'local',
              at: Date.now(),
            }
            return
          } catch (error) {
            lastError = error
            if (attempt < maxAttempts - 1) {
              await new Promise((resolve) => setTimeout(resolve, 350))
              continue
            }
          }
        }

        if (this.qtyPickerWeightReqId !== reqId || !this.qtyPickerOpen) return
        if (lastError) {
          notifyError(lastError, this.$t('cash.notifications.weightReadFailed'))
          return
        }
        if (gotZero && !silent) {
          notify(this.$t('cash.notifications.scaleZeroWeight'), 'warning')
        }
      } finally {
        if (this.qtyPickerWeightReqId === reqId) {
          this.qtyPickerScaleLoading = false
        }
      }
    },
    confirmQtyPicker() {
      const product = this.qtyPickerProduct
      if (!product) return
      const modifiers = this.qtyPickerModifiers
      const raw = String(this.qtyPickerValue || '').trim()
      const amount = Number(raw)
      if (!raw || !amount || amount <= 0) {
        notify(this.$t('cash.notifications.enterWeightOrLiter'), 'warning')
        return
      }
      const available = this.productAvailableQty(product)
      if (amount > available) {
        notify(this.$t('cash.notifications.onlyInStock', { quantity: available }), 'warning')
        return
      }
      this.closeQtyPicker()
      this.pushOrderLine(product, modifiers, amount)
    },
    recalcOrderPrice(order) {
      const unit = this.unitPrice(order)
      const base = this.productUnitBase(order)
      const discount = this.lineDiscount(order)
      const total = Math.round(unit * this.orderQty(order) * 100) / 100
      this.$set(order, 'catalog_unit_price', unit)
      this.$set(order, 'catalog_order_price', total)
      this.$set(order, 'display_unit_price', unit)
      this.$set(order, 'display_unit_base', base)
      this.$set(order, 'display_line_discount', discount)
      this.$set(order, 'order_price', total)
    },
    syncPaymentsToDue() {
      if (!this.payments.length) return
      if (this.payments.length === 1) {
      this.payments[0].amount = this.amount_of_order
        return
      }
      if (this.payments[0]) {
        this.payments[0].amount = this.amount_of_order
      }
      for (let i = 1; i < this.payments.length; i += 1) {
        this.payments[i].amount = 0
      }
    },
    applyPaidTotal(rawTarget) {
      if (this.isQuickSaleMode) {
        const value = Math.max(0, Math.round((Number(rawTarget) || 0) * 100) / 100)
        this.amount_of_order = value
        this.syncPaymentsToDue()
        this.schedulePersist()
        return true
      }

      this.orders.forEach((order) => this.recalcOrderPrice(order))
      const catalog = this.catalogSubtotal
      let target = Math.round((Number(rawTarget) || 0) * 100) / 100

      if (target <= 0) {
        notify(this.$t('cash.notifications.enterSaleAmount'), 'warning')
        this.getAmountOfOrder()
        return false
      }
      if (target > catalog + 0.009) {
        notify(this.$t('cash.notifications.dueTooHigh', { max: catalog }), 'warning')
        target = catalog
      }

      if (catalog <= 0 || Math.abs(target - catalog) < 0.009) {
        this.amount_of_order = catalog
        this.syncPaymentsToDue()
        this.schedulePersist()
        return true
      }

      const ratio = target / catalog
      let allocated = 0
      const lastIndex = this.orders.length - 1

      this.orders.forEach((order, index) => {
        const qty = Number(this.orderQty(order)) || 0
        const catalogLine = Number(order.catalog_order_price) || 0
        let newLineTotal
        if (index === lastIndex) {
          newLineTotal = Math.round((target - allocated) * 100) / 100
        } else {
          newLineTotal = Math.round(catalogLine * ratio * 100) / 100
          allocated += newLineTotal
        }

        const newUnit = qty > 0 ? Math.round((newLineTotal / qty) * 100) / 100 : 0
        const base = Number(order.display_unit_base) || this.productUnitBase(order)
        const lineDisc = Math.max(0, Math.round((base * qty - newLineTotal) * 100) / 100)

        this.$set(order, 'display_unit_price', newUnit)
        this.$set(order, 'order_price', newLineTotal)
        this.$set(order, 'display_line_discount', lineDisc)
      })

      this.amount_of_order = target
      this.syncPaymentsToDue()
      this.schedulePersist()
      return true
    },
    onDueAmountChange(event) {
      const value = Number(String((event && event.target && event.target.value) || '').replace(',', '.'))
      this.applyPaidTotal(value)
    },
    openDuePad() {
      if (!this.touchMode) return
      this.paymentPadIndex = 'due'
    },
    applyDiscount(order, value) {
      this.$set(order, 'discount_id', value ? Number(value) : null)
      this.recalcOrderPrice(order)
      this.getAmountOfOrder()
      this.schedulePersist()
    },
    addSale() {
      this.saleCounter += 1
      const sale = createSale(this.saleCounter)
      this.sales.push(sale)
      this.activeSaleId = sale.id
      this.schedulePersist()
    },
    switchSale(id) {
      this.activeSaleId = id
      this.getAmountOfOrder()
      this.schedulePersist()
    },
    closeSale(id) {
      if (this.sales.length <= 1) return

      const index = this.sales.findIndex((sale) => sale.id === id)
      if (index === -1) return

      this.sales.splice(index, 1)
      if (this.activeSaleId === id) {
        const next = this.sales[Math.max(0, index - 1)]
        this.activeSaleId = next.id
      }
      this.getAmountOfOrder()
      this.schedulePersist()
    },
    resetActiveSale() {
      this.activeSale.orders = []
      this.activeSale.give_bonus = false
      this.activeSale.phone = ''
      this.activeSale.bonus = ''
      this.activeSale.amount_of_order = 0
      this.activeSale.payments = [{ amount: 0, selected_payment: '' }]
      this.selected_payment = ''
      this.clearLoanDraft()
      this.schedulePersist()
    },
    allPaymentsSelected() {
      return this.payments.length > 0 && this.payments.every((payment) => payment.selected_payment !== '')
    },
    paymentTypeLabel(value) {
      const type = this.payment_types.find((item) => item.value === value)
      return (type && type.label) || value || ''
    },
    isPaymentTypeSelected(value) {
      return this.payments.some((payment) => payment.selected_payment === value)
    },
    selectedPaymentsCount() {
      return this.payments.filter((payment) => payment.selected_payment).length
    },
    shouldShowPaymentAmount(payment) {
      return !!(payment && payment.selected_payment)
    },
    togglePaymentType(value) {
      const index = this.payments.findIndex((payment) => payment.selected_payment === value)

      if (index !== -1) {
        if (this.payments.length === 1) {
          this.payments[0].selected_payment = ''
          this.payments[0].amount = this.amount_of_order
      } else {
          this.payments.splice(index, 1)
          if (this.payments.length === 1 && this.payments[0].selected_payment) {
            this.payments[0].amount = this.amount_of_order
          }
        }
        return
      }

      const emptyIndex = this.payments.findIndex((payment) => !payment.selected_payment)
      if (emptyIndex !== -1) {
        this.payments[emptyIndex].selected_payment = value
        if (this.selectedPaymentsCount() === 1) {
          this.payments[emptyIndex].amount = this.amount_of_order
        }
      } else {
        if (this.paidTotal > this.amount_of_order) return
        this.payments.push({
          amount: 0,
          selected_payment: value,
        })
      }

      if (this.isLoanType(value)) {
        this.prepareLoanDraft({ openModal: true })
        this.show_loan = true
      } else {
        const padIndex = this.payments.findIndex((payment) => payment.selected_payment === value)
        if (padIndex !== -1) {
          this.$nextTick(() => this.openPaymentPad(padIndex))
        }
      }
    },
    setPaymentValue(index, amount) {
      if (index === 0 && this.payments[1]) {
        this.payments[1].amount = this.amount_of_order - amount
      }
    },
    openPaymentPad(index) {
      if (!this.touchMode) return
      this.paymentPadIndex = index
    },
    closePaymentPad() {
      this.paymentPadIndex = null
    },
    onPaymentPadInput(value) {
      if (this.paymentPadIndex === null) return
      const amount = Number(String(value || '').replace(/\D/g, '')) || 0
      if (this.paymentPadIndex === 'due') {
        this.applyPaidTotal(amount)
        return
      }
      if (!this.payments[this.paymentPadIndex]) return
      this.payments[this.paymentPadIndex].amount = amount
      this.setPaymentValue(this.paymentPadIndex, amount)
    },
    onPaymentAmountTyping(index, event) {
      const amount = Number(String((event && event.target && event.target.value) || '').replace(/\D/g, '')) || 0
      if (!this.payments[index]) return
      this.payments[index].amount = amount
      this.setPaymentValue(index, amount)
    },
    removePayment(index) {
      if (this.payments.length <= 1) return
      this.payments.splice(index, 1)
    },
    totalAmount() {
      return this.paidTotal
    },
    addPayment() {
      if (this.paidTotal > this.amount_of_order) return false
      this.payments.push({
        amount: 0,
        selected_payment: '',
      })
    },
    subBonusPart() {
      if (!this.bonusClient) return false
      if (this.amount_bonus > this.bonusClient.bonus) {
        this.bonus_message = this.$t('cash.notifications.amountExceedsBonus')
        return false
      }
      this.bonusClient.bonus = this.bonusClient.bonus - this.amount_bonus
      const next = Math.max(0, Math.round((Number(this.amount_of_order) - Number(this.amount_bonus)) * 100) / 100)
      this.applyPaidTotal(next)
      this.show_bonus = false
    },
    subBonus(bonus) {
      const next = Math.max(0, Math.round((Number(this.amount_of_order) - Number(bonus)) * 100) / 100)
      this.applyPaidTotal(next)
      if (this.bonusClient) this.bonusClient.bonus = 0
    },
    formatBonusPhone(phone) {
      const digits = String(phone || '').replace(/\D/g, '')
      if (digits.length !== 11) return phone || '—'
      return `${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`
    },
    onBonusSearchInput(event) {
      const raw = String(event.target.value || '')
      const digits = raw.replace(/\D/g, '')
      this.phone = digits
      this.bonus = ''

      if (this.bonusSearchTimer) clearTimeout(this.bonusSearchTimer)

      // Карта: 5 цифр (дольше debounce, чтобы не срабатывало при наборе телефона)
      // Телефон: 11 цифр
      if (digits.length !== 5 && digits.length !== 11) return

      const delay = digits.length === 5 ? 700 : 200
      this.bonusSearchTimer = setTimeout(() => {
        // Если уже дописали дальше 5 цифр — это не карта
        if (digits.length === 5 && String(this.phone || '').replace(/\D/g, '').length !== 5) {
          return
        }
        this.searchBonus(digits)
      }, delay)
    },
    searchBonus(val) {
      const query = String(val || '').replace(/\D/g, '')
      if (query.length !== 5 && query.length !== 11) return

      this.getAmountOfOrder()
      http
        .get(`bonus/search?search=${encodeURIComponent(query)}`)
          .then((res) => {
          const list = Array.isArray(res.data) ? res.data : []
          if (!list.length) {
            this.bonus = '__not_found__'
            this.openBonusClientModal({
              phone: query.length === 11 ? query : '',
            })
            return
          }

          const exact = list.find(
            (item) =>
              String(item.card_number || '') === query ||
              String(item.phone || '').replace(/\D/g, '') === query
          )
          const client = exact || list[0]
          this.bonus = [client]
          this.phone = String(client.phone || '').replace(/\D/g, '') || query
        })
        .catch(() => {
          this.bonus = '__not_found__'
        })
    },
    async openBonusClientModal(prefill = {}) {
      const phoneDigits = String(prefill.phone || this.phone || '').replace(/\D/g, '')
      let phoneMasked = ''
      if (phoneDigits.length === 11) {
        phoneMasked = `8(${phoneDigits.slice(1, 4)}) ${phoneDigits.slice(4, 7)}-${phoneDigits.slice(7, 9)}-${phoneDigits.slice(9)}`
      }

      this.bonusClientForm = {
        name: prefill.name || '',
        phone: phoneMasked,
        card_number: '',
      }
      this.bonusClientOpen = true
      await this.loadBonusCardNumber()
    },
    async loadBonusCardNumber() {
      this.bonusClientLoading = true
      try {
        const { data } = await http.get('bonus/card/generate')
        this.bonusClientForm.card_number = data.card_number || ''
      } catch (error) {
        notifyError(error)
      } finally {
        this.bonusClientLoading = false
      }
    },
    async saveBonusClient() {
      const name = (this.bonusClientForm.name || '').trim()
      const phone = String(this.bonusClientForm.phone || '').replace(/\D/g, '')
      const card = String(this.bonusClientForm.card_number || '').replace(/\D/g, '')

      if (!name) {
        notify(this.$t('cash.notifications.enterFullName'), 'warning')
        return
      }
      if (phone.length !== 11) {
        notify(this.$t('cash.notifications.enterPhone'), 'warning')
        return
      }
      if (card.length !== 5) {
        notify(this.$t('cash.notifications.waitForCardNumber'), 'warning')
        return
      }

      this.bonusClientSaving = true
      try {
        const { data } = await http.post('bonus/client', {
          name,
          phone,
          card_number: card,
        })
        const client = data.client
        this.bonus = [client]
        this.phone = String(client.phone || '').replace(/\D/g, '')
        this.bonusClientOpen = false
        notify(data.message || this.$t('cash.notifications.clientAdded'))
      } catch (error) {
        notifyError(error)
      } finally {
        this.bonusClientSaving = false
      }
    },
    async saveOrder() {
      if (
        this.payments.length > 1 &&
        (this.paidTotal > this.amount_of_order || this.paidTotal < this.amount_of_order)
      ) {
        notify(this.$t('cash.notifications.invalidPayment'), 'warning')
        return false
      }

      if (this.isQuickSaleMode) {
        if (!Number(this.amount_of_order) || Number(this.amount_of_order) <= 0) {
          notify(this.$t('cash.notifications.enterSaleAmount'), 'warning')
          return false
        }
      } else {
        const hasZeroAmount = this.orders.some(
          (order) => order.count_type !== 'count' && (!order.amount_value || Number(order.amount_value) === 0)
        )
        if (this.orders.length === 0) {
          notify(this.$t('cash.notifications.selectProducts'), 'warning')
          return false
        }
        if (hasZeroAmount) {
          notify(this.$t('cash.notifications.enterWeightOrLiter'), 'warning')
          return false
        }
      }

      if (!this.allPaymentsSelected()) {
        notify(this.$t('cash.notifications.selectPaymentType'), 'warning')
        return false
      }
      if (this.hasLoanPayment) {
        if (!this.name) {
          notify(this.$t('cash.notifications.enterDebtorName'), 'warning')
          return false
        }
        if (!this.phone_loan || this.phone_loan.replace(/\D/g, '').length !== 11) {
          notify(this.$t('cash.notifications.enterDebtorPhone'), 'warning')
          return false
        }
        const incompleteExtra = (this.extra_phones_loan || []).some((item) => {
          const digits = String(item || '').replace(/\D/g, '')
          return digits.length > 0 && digits.length !== 11
        })
        if (incompleteExtra) {
          notify(this.$t('cash.notifications.enterDebtorPhone'), 'warning')
          return false
        }
        if (!this.loan || Number(this.loan) <= 0) {
          notify(this.$t('cash.notifications.enterDebtAmount'), 'warning')
          return false
        }
        if (!this.date) {
          notify(this.$t('cash.notifications.enterDebtReturnDate'), 'warning')
          return false
        }
      }
      if (this.give_bonus) {
        if (!this.bonusClient) {
          notify(this.$t('cash.notifications.findOrAddBonusClient'), 'warning')
          return false
        }
        const phoneDigits = String(this.bonusClient.phone || this.phone || '').replace(/\D/g, '')
        if (phoneDigits.length < 10) {
          notify(this.$t('cash.notifications.clientPhoneMissing'), 'warning')
          return false
        }
      }

      this.paying = true
      try {
        const primaryPayment = this.payments[0] && this.payments[0].selected_payment
        const bonusPhone = this.bonusClient
          ? String(this.bonusClient.phone || '').replace(/\D/g, '')
          : String(this.phone || '').replace(/\D/g, '')
        const { data } = await http.post('order', {
          orders: this.isQuickSaleMode ? [] : this.orders,
        total: this.amount_of_order,
          payment_type: primaryPayment || this.selected_payment,
        loan: this.loan,
        phone_loan: this.phone_loan,
        extra_phones: this.normalizedLoanExtraPhones(),
        date: this.date,
          phone: bonusPhone,
          card_number: this.bonusClient ? this.bonusClient.card_number : '',
        give_bonus: this.give_bonus,
        name: this.name,
          bonus_name: this.bonusClient ? this.bonusClient.name : '',
          show_loan: this.hasLoanPayment,
          bonus: this.bonusClient ? this.bonusClient.bonus : '',
          payments: JSON.stringify(this.payments),
        })

        notify(data.message || this.$t('cash.notifications.paymentSucceeded'))
        this.createReceipt(data)
        this.productCache = Object.create(null)
        localStorage.removeItem(loanDraftStorageKey())
        if (this.sales.length > 1) {
          this.closeSale(this.activeSaleId)
        } else {
          this.resetActiveSale()
        }
        if (!this.isQuickSaleMode && this.category_id) {
          this.fetchProducts({ useCache: false })
        }
      } catch (error) {
        notifyError(error)
      } finally {
        this.paying = false
      }
    },
    getAmountOfOrder() {
      this.orders.forEach((order) => this.recalcOrderPrice(order))
      this.amount_of_order = this.orders.reduce(
        (total, order) => total + (Number(order.order_price) || 0),
        0
      )
      this.syncPaymentsToDue()
      this.schedulePersist()
    },
    deleteElement(lineKey) {
      const index = this.orders.findIndex((order) => (order.line_uid || order.id) === lineKey)
      if (index !== -1) {
        this.orders.splice(index, 1)
        this.getAmountOfOrder()
      }
    },
    changeMass(order) {
      if (order.quantity < order.amount_value) {
        notify(this.$t('cash.notifications.insufficientStock'), 'warning')
        order.amount_value = order.quantity
      }
      this.recalcOrderPrice(order)
      this.getAmountOfOrder()
    },
    onCartWeightInput(order) {
      const available = Number(order.quantity) || 0
      const amount = Number(order.amount_value) || 0
      if (available && amount > available) {
        this.$set(order, 'amount_value', available)
      }
      this.recalcOrderPrice(order)
      this.scheduleCartTotal()
    },
    scheduleCartTotal: debounce(function () {
      this.getAmountOfOrder()
    }, 200),
    stopScalePolling() {
      if (this.scalePollTimer) {
        window.clearInterval(this.scalePollTimer)
        this.scalePollTimer = null
      }
    },
    async refreshScaleNow() {
      if (this.scalePeekBusy) return
      this.scalePeekBusy = true
      try {
        this.scaleLive = await peekLocalWeight()
      } finally {
        this.scalePeekBusy = false
      }
    },
    applyWeightToOrder(order, weight) {
      const value = Number(weight && weight.value)
      if (!value || value <= 0) {
        notify(this.$t('cash.notifications.scaleZeroWeight'), 'warning')
        return false
      }
      if (order.quantity < value) {
        notify(this.$t('cash.notifications.insufficientStock'), 'warning')
        return false
      }
      this.$set(order, 'amount_value', value)
      this.changeMass(order)
      const stableText = weight.stable
        ? this.$t('cash.scale.stable')
        : this.$t('cash.scale.unstable')
      notify(this.$t('cash.notifications.weightRead', {
        value,
        unit: weight.unit || 'kg',
        stability: stableText,
      }))
      if (weight.source === 'local') {
        this.scaleLive = {
          value,
          unit: weight.unit || 'kg',
          stable: !!weight.stable,
          source: 'local',
          at: Date.now(),
        }
      }
      return true
    },
    async readWeightForOrder(order) {
      this.scaleLoadingId = order.line_uid || order.id
      try {
        const weight = await readScaleWeight()
        this.applyWeightToOrder(order, weight)
      } catch (error) {
        notifyError(error, this.$t('cash.notifications.weightReadFailed'))
      } finally {
        this.scaleLoadingId = null
      }
    },
    addOrderCount(order) {
      if (order.quantity === 0 || order.quantity < 0) {
        notify(this.$t('cash.notifications.noStock'), 'warning')
        return false
      }
      const next = (Number(order.count_value) || 1) + 1
      if (next > Number(order.quantity)) {
        notify(this.$t('cash.notifications.onlyInStock', { quantity: order.quantity }), 'warning')
        return false
      }
      if (Number(order.quantity) === 1) {
        this.$set(order, 'count_value', 1)
        this.recalcOrderPrice(order)
        this.getAmountOfOrder()
        return false
      }
      this.$set(order, 'count_value', next)
      this.recalcOrderPrice(order)
      this.getAmountOfOrder()
    },
    subOrderCount(order) {
      if (!order.count_value) {
        this.$set(order, 'count_value', 1)
        this.getAmountOfOrder()
        return false
      }
      if (order.count_value <= 1) {
        order.count_value = 1
        this.getAmountOfOrder()
        return false
      }
        order.count_value = order.count_value - 1
      this.recalcOrderPrice(order)
      this.getAmountOfOrder()
    },
    addOrder(product) {
      const modifiers = this.productModifiers(product)
      if (modifiers.length) {
        this.modifierPickerProduct = product
        this.modifierPickerSelected = []
        this.modifierPickerOpen = true
        return
      }
      this.openQtyPicker(product, [])
    },
    pushOrderLine(product, modifiers = [], qty = null) {
      const normalized = normalizeModifiers(modifiers)
      const lineUid = buildLineUid(product.id, normalized)
      const existingOrder = this.orders.find((order) => (order.line_uid || buildLineUid(order.id, order.modifiers)) === lineUid)
      const amount = qty == null ? null : Number(qty)

      if (existingOrder) {
        if (existingOrder.count_type === 'count') {
          const addBy = amount && amount > 0 ? amount : 1
          const next = (Number(existingOrder.count_value) || 0) + addBy
          if (next > Number(existingOrder.quantity)) {
            notify(this.$t('cash.notifications.onlyInStock', { quantity: existingOrder.quantity }), 'warning')
            return
          }
          this.$set(existingOrder, 'count_value', next)
          this.recalcOrderPrice(existingOrder)
          this.getAmountOfOrder()
        } else if (amount && amount > 0) {
          if (amount > Number(existingOrder.quantity)) {
            notify(this.$t('cash.notifications.onlyInStock', { quantity: existingOrder.quantity }), 'warning')
            return
          }
          this.$set(existingOrder, 'amount_value', amount)
          this.recalcOrderPrice(existingOrder)
          this.getAmountOfOrder()
        }
        return
      }

      const available = this.productAvailableQty(product)
      if (available <= 0) {
        notify(this.$t('cash.notifications.noStockRestock'), 'warning')
        return
      }

      const isCount = (product.count_type || 'count') === 'count'
      const countValue = isCount ? (amount && amount > 0 ? amount : 1) : 1
      const amountValue = isCount ? 0 : (amount && amount > 0 ? amount : 0)

      if (isCount && countValue > available) {
        notify(this.$t('cash.notifications.onlyInStock', { quantity: available }), 'warning')
        return
      }
      if (!isCount && amountValue > available) {
        notify(this.$t('cash.notifications.onlyInStock', { quantity: available }), 'warning')
        return
      }
      if (!isCount && (!amountValue || amountValue <= 0)) {
        notify(this.$t('cash.notifications.enterWeightOrLiter'), 'warning')
        return
      }

      const byproductsLabel = this.productByproductsLabel(product)
      const line = {
        line_uid: lineUid,
        id: product.id,
        name: product.name,
        price: Number(product.price) || 0,
        quantity: available,
        count_type: product.count_type || 'count',
        amount_value: amountValue,
        count_value: countValue,
        order_price: 0,
        discount_id: null,
        product_discount: product.product_discount_value
          ? {
            type: product.product_discount_type,
            value: Number(product.product_discount_value),
          }
          : null,
        byproducts_label: byproductsLabel || null,
        modifiers: normalized,
        modifier_ids: normalized.map((item) => item.id),
      }
      this.recalcOrderPrice(line)
      this.orders.push(line)
      this.getAmountOfOrder()
    },
  },
  watch: {
    show_loan(isOpen) {
      if (!isOpen) this.closeLoanKeyboard()
    },
    name() {
      this.schedulePersistLoanDraft()
    },
    loan() {
      this.schedulePersistLoanDraft()
    },
    phone_loan() {
      this.schedulePersistLoanDraft()
    },
    date() {
      this.schedulePersistLoanDraft()
    },
    loanPaymentSelectedKey(next, prev) {
      if (next === prev) return
      this.syncLoanPaymentSelection(this.hasLoanPayment)
    },
    search(val) {
      if (val) {
        this.debouncedSearch()
      } else if (this.category_id) {
        this.setPageQuiet(1)
        this.fetchProducts({ useCache: true })
      } else {
        this.applyProducts([], 0)
      }
    },
    page() {
      if (this.ignorePageWatch || this.search) return
      if (this.category_id) {
        this.fetchProducts({ useCache: true })
      }
    },
    activeSaleId() {
      this.getAmountOfOrder()
      this.schedulePersist()
    },
    cameraScannerOpen(isOpen) {
      if (!isOpen && this.cameraScannerFallbackTimer) {
        window.clearTimeout(this.cameraScannerFallbackTimer)
        this.cameraScannerFallbackTimer = null
      }
    },
  },
}
</script>


<style lang="scss" scoped>
.pos {
  --ink: #122033;
  --muted: #6a7b90;
  --accent: #0b4f8a;
  --accent-soft: #e7f1f8;
  --line: #d7e2ee;
  --surface: #edf2f7;
  --card: #ffffff;
  --pay: #d97706;

  min-height: 100vh;
  min-height: -webkit-fill-available;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  color: var(--ink);
  background:
    radial-gradient(circle at top right, rgba(11, 79, 138, 0.08), transparent 28%),
    linear-gradient(180deg, #f7f9fc 0%, var(--surface) 100%);
}

.pos__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 18px 28px;
  padding-top: calc(18px + env(safe-area-inset-top, 0px));
  padding-left: calc(28px + env(safe-area-inset-left, 0px));
  padding-right: calc(28px + env(safe-area-inset-right, 0px));
  background:
    radial-gradient(circle at 12% 20%, rgba(90, 168, 220, 0.28), transparent 34%),
    linear-gradient(135deg, #163a52 0%, #10243a 55%, #0d1c2e 100%);
  color: #fff;
  box-shadow: 0 10px 30px rgba(16, 36, 58, 0.18);
}

.pos__brand {
  display: flex;
  align-items: center;
  gap: 14px;

  h1 {
    margin: 0;
    font-size: 22px;
    font-weight: 750;
    letter-spacing: -0.02em;
  }

  p {
    margin: 3px 0 0;
    font-size: 12px;
    opacity: 0.72;
  }
}

.pos__brand-mark {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: linear-gradient(180deg, #fff 0%, #eef5fb 100%);
  color: var(--accent);
  font-weight: 800;
  font-size: 20px;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.12);
}

.pos__header-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  margin-left: auto;
}

.pos__live {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 12px;
  font-weight: 650;

  i {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #34d399;
    box-shadow: 0 0 0 4px rgba(52, 211, 153, 0.18);
  }
}

.pos__touch-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.1);
  color: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;

  input[type='checkbox'] {
    width: 15px;
    height: 15px;
    margin: 0;
    accent-color: #38bdf8;
    cursor: pointer;
  }

  &.is-on {
    background: rgba(56, 189, 248, 0.22);
    border-color: rgba(56, 189, 248, 0.55);
    color: #e0f2fe;
  }
}

.pos__scale-live {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.1);
  color: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  .mdi {
    font-size: 16px;
  }

  &--ok {
    background: rgba(52, 211, 153, 0.18);
    border-color: rgba(52, 211, 153, 0.45);
  }

  &--wait {
    background: rgba(251, 191, 36, 0.16);
    border-color: rgba(251, 191, 36, 0.4);
  }

  &--off {
    opacity: 0.75;
  }
}

.pos__paper {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 12px;
  font-weight: 650;
  color: inherit;

  span {
    white-space: nowrap;
    opacity: 0.85;
  }

  select {
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
    outline: none;
  }
}

.pos__cashier {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 12px;
  font-weight: 650;

  .mdi {
    font-size: 15px;
  }
}

.pos__header-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
}

.pos__icon-btn {
  width: 42px;
  height: 42px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;

  .mdi {
    font-size: 20px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.16);
    transform: translateY(-1px);
  }
}

.pos__body {
  flex: 1;
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 18px;
  min-height: 0;
  padding: 18px 20px 20px;
  padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  padding-left: calc(20px + env(safe-area-inset-left, 0px));
  padding-right: calc(20px + env(safe-area-inset-right, 0px));
}

.pos__catalog,
.pos__cart {
  min-height: 0;
  overflow: auto;
}

.pos__panel,
.pos__cart-shell {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 120px);
  min-height: calc(100dvh - 120px);
  padding: 20px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(215, 226, 238, 0.9);
  border-radius: 22px;
  box-shadow: 0 14px 40px rgba(18, 32, 51, 0.06);
  backdrop-filter: blur(8px);
}

.pos__quick-hint {
  margin: 0 0 20px;
  color: #6b7a90;
  font-size: 14px;
  line-height: 1.5;
}

.pos__quick-amount {
  display: block;
  margin-bottom: 22px;

  > span {
    display: block;
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 700;
    color: #152033;
  }

  input {
  width: 100%;
    padding: 18px 16px;
    border: 1.5px solid #d9e2ec;
    border-radius: 16px;
    font-size: 28px;
    font-weight: 800;
    color: #152033;
    background: #fbfdff;
    outline: none;

    &:focus {
      border-color: #0b4f8a;
      box-shadow: 0 0 0 4px rgba(11, 79, 138, 0.1);
    }
  }
}

.pos__quick-points {
  list-style: none;
  margin: 0;
  padding: 0;
    display: flex;
    flex-direction: column;
  gap: 10px;

  li {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #6b7a90;
    font-size: 13.5px;
  }

  .mdi {
    color: #0b4f8a;
    font-size: 18px;
  }
}

.pos__quick-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 16px 18px;
  border-radius: 16px;
  background: #eef4fa;

  span {
    color: #6b7a90;
    font-size: 14px;
    font-weight: 650;
  }

  strong {
    font-size: 24px;
    font-weight: 800;
    color: #152033;
  }
}

.pos__cart-shell {
  display: flex;
  flex-direction: column;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(241, 246, 251, 0.96) 100%);
}

.pos__tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.pos__tabs-list {
  display: flex;
  flex: 1;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 2px;
}

.pos__tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 9px 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
  color: var(--ink);
  font-size: 13px;
  font-weight: 650;
  cursor: pointer;
  transition: all 0.18s ease;

  em {
    font-style: normal;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: var(--accent-soft);
    color: var(--accent);
    font-size: 11px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .mdi {
    font-size: 14px;
    opacity: 0.5;
  }

  .mdi:hover {
    opacity: 1;
    color: #b42318;
  }

  &--active {
    background: var(--accent);
    border-color: var(--accent);
    color: #fff;
    box-shadow: 0 8px 18px rgba(11, 79, 138, 0.22);

    em {
      background: rgba(255, 255, 255, 0.2);
      color: #fff;
    }

    .mdi {
      color: #fff;
      opacity: 0.9;
    }
  }
}

.pos__tab-add {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border: 1px dashed #9eb6cc;
  border-radius: 12px;
  background: #fff;
  color: var(--accent);
  display: grid;
  place-items: center;
  cursor: pointer;

  .mdi {
    font-size: 18px;
  }

  &:hover {
    border-style: solid;
    background: var(--accent-soft);
  }
}

.pos__section-title {
  margin-bottom: 10px;
  font-size: 11px;
  font-weight: 750;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);

  &--inline {
    margin-bottom: 0;
  }
}

.pos__section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin: 8px 0 14px;
}

.pos__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

.pos__chip {
  padding: 9px 14px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: #fff;
  font-weight: 650;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    border-color: #9eb6cc;
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }

  &--active {
    background: var(--accent);
    border-color: var(--accent);
    color: #fff;
    box-shadow: 0 8px 16px rgba(11, 79, 138, 0.18);
  }
}

.pos__search-wrap {
    position: relative;
  width: min(320px, 100%);

  > .mdi-magnify {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #8aa0b5;
    font-size: 18px;
    z-index: 1;
  }
}

.pos__catalog-actions {
      display: flex;
      align-items: center;
  gap: 8px;
  width: min(370px, 100%);
}

.pos__camera-btn {
  display: grid;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  place-items: center;
  border: 0;
  border-radius: 9px;
  background: var(--accent-soft);
  color: var(--accent);
  cursor: pointer;
  transition: background .18s ease, color .18s ease;

  &:hover {
    background: var(--accent);
    color: #fff;
  }

  .mdi {
    font-size: 19px;
  }
}

.pos__search-kb-btn {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #0b4f8a;
        cursor: pointer;
  display: grid;
  place-items: center;

  &:hover {
    background: var(--accent-soft);
  }

  .mdi {
        font-size: 18px;
      }
    }

.pos__search {
  width: 100%;
  padding: 11px 40px 11px 38px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
  outline: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:focus {
    border-color: #8eb0cc;
    box-shadow: 0 0 0 4px rgba(11, 79, 138, 0.08);
  }
}

.pos-scanner {
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
}

.pos-scanner__header {
        display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 20px 14px;

  h2,
  p {
    margin: 0;
  }

  h2 {
    color: var(--ink);
    font-size: 19px;
  }

  p {
    margin-top: 5px;
    color: var(--muted);
    font-size: 13px;
    line-height: 1.4;
  }

  button {
    display: grid;
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    place-items: center;
    border: 0;
    border-radius: 9px;
    background: #eef3f7;
    color: var(--muted);
  cursor: pointer;
  }
}

.pos-scanner__camera {
  position: relative;
  min-height: 250px;
  overflow: hidden;
  background: #152033;

  ::v-deep video {
    display: block;
    width: 100%;
    min-height: 250px;
    object-fit: cover;
  }
}

.pos-scanner__loading {
  position: absolute;
  inset: 0;
  display: flex;
        align-items: center;
  justify-content: center;
  gap: 8px;
  background: #152033;
  color: #fff;
  font-size: 13px;
  z-index: 2;
}

.pos-scanner__frame {
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(72%, 280px);
  aspect-ratio: 1.75;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 1;

  i {
    position: absolute;
    width: 22px;
    height: 22px;
    border-color: #7ecbff;
    border-style: solid;
  }

  i:nth-child(1) {
    top: 0;
    left: 0;
    border-width: 3px 0 0 3px;
    border-radius: 8px 0 0;
  }

  i:nth-child(2) {
    top: 0;
    right: 0;
    border-width: 3px 3px 0 0;
    border-radius: 0 8px 0 0;
  }

  i:nth-child(3) {
    bottom: 0;
    left: 0;
    border-width: 0 0 3px 3px;
    border-radius: 0 0 0 8px;
  }

  i:nth-child(4) {
    right: 0;
    bottom: 0;
    border-width: 0 3px 3px 0;
    border-radius: 0 0 8px;
  }

  span {
    position: absolute;
    top: 50%;
    right: 8px;
    left: 8px;
    height: 2px;
    background: #ff5c5c;
    box-shadow: 0 0 10px rgba(255, 92, 92, .8);
    animation: scanner-line 1.8s ease-in-out infinite;
  }
}

@keyframes scanner-line {
  0%,
  100% { transform: translateY(-40px); }
  50% { transform: translateY(40px); }
}

.pos-scanner__close {
  width: calc(100% - 32px);
  margin: 16px;
  padding: 11px;
  border: 0;
  border-radius: 10px;
  background: #edf3f8;
  color: #405168;
  font-weight: 700;
  cursor: pointer;
}

.pos__products {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(156px, 1fr));
  gap: 12px;
  transition: opacity 0.15s ease;

  &--loading {
    opacity: 0.55;
    pointer-events: none;
  }
}

.pos__product {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  min-height: 118px;
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: linear-gradient(180deg, #fff 0%, #f8fbfe 100%);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    border-color: #9eb6cc;
    transform: translateY(-3px);
    box-shadow: 0 12px 24px rgba(18, 32, 51, 0.08);

    .pos__product-add {
      background: var(--accent);
      color: #fff;
    }
  }
}

.pos__product-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: flex-start;
}

.pos__product-info {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 9px;
}

.pos__product-image {
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  border-radius: 10px;
  object-fit: cover;
}

.pos__product-text {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.pos__product-name {
  font-weight: 730;
  line-height: 1.35;
}

.pos__product-together {
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
}

.pos__product-add {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: 9px;
  background: var(--accent-soft);
  color: var(--accent);
  display: grid;
  place-items: center;
  transition: all 0.18s ease;

  .mdi {
    font-size: 16px;
  }
}

.pos__product-meta {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.pos__product-price {
  color: var(--accent);
  font-weight: 780;
  font-size: 16px;
}

.pos__product-stock {
  color: var(--muted);
  font-size: 12px;
  font-weight: 650;
}

.pos__empty,
.pos__cart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 240px;
  padding: 28px;
  border: 1px dashed #c3d2e1;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.7);
  color: var(--muted);
  text-align: center;

  .mdi {
    font-size: 40px;
    color: #9eb6cc;
  }

  p {
    margin: 0;
    color: var(--ink);
    font-weight: 750;
  }

  span {
    font-size: 13px;
  }
}

.pos__cart-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;

  h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 780;
    letter-spacing: -0.02em;
  }

  p {
    margin: 4px 0 0;
    color: var(--muted);
    font-size: 13px;
  }
}

.pos__cart-count {
  padding: 6px 10px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent);
  font-size: 12px;
  font-weight: 700;
}

.pos__cart-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
  max-height: 38vh;
  overflow: auto;
  padding-right: 2px;
}

.pos__cart-item {
  padding: 14px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  box-shadow: 0 6px 16px rgba(18, 32, 51, 0.03);
}

.pos__cart-item-top {
  display: flex;
  justify-content: space-between;
  gap: 12px;

  h3 {
    margin: 0 0 4px;
    font-size: 15px;
    font-weight: 730;
  }
}

.pos__cart-unit {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
}

.pos__cart-together {
  margin: 0 0 4px;
  color: #5b6b82;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
}

.pos__cart-mods {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin: 0 0 4px;
  color: #5b6b82;
  font-size: 12px;
}

.pos-modifier-option {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  padding: 10px 12px;
  border: 1px solid #d9e2ec;
  border-radius: 12px;
  cursor: pointer;
}

.pos-modifier-option input {
  margin: 0;
}

.pos-modifier-option span {
  flex: 1;
}

.pos-modifier-option strong {
  color: #0b4f8a;
}

.pos-modifier-total {
  margin: 12px 0 0;
  font-weight: 700;
  color: #152033;
}

.pos-qty-dialog {
  width: 320px !important;
  max-width: calc(100vw - 32px) !important;
  margin: 0 auto !important;
  border-radius: 22px !important;
  overflow: hidden;
  box-shadow: 0 18px 48px rgba(18, 32, 51, 0.22) !important;
}

.qty-modal {
  padding: 16px 14px 14px;
  background: #fff;
}

.qty-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;

  h2 {
    margin: 4px 0 0;
    font-size: 17px;
    font-weight: 800;
    color: #122033;
    line-height: 1.25;
  }
}

.qty-modal__hint {
  margin: 0;
  color: #6a7b90;
  font-size: 12px;
  font-weight: 700;
}

.qty-modal__close {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 10px;
  background: #eef3f8;
  color: #5b6b82;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #e3ebf3;
  }
}

.qty-modal__display {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #f4f7fb;
  border: 1px solid #d7e2ee;

  strong {
    font-size: 36px;
    line-height: 1;
    font-weight: 800;
    color: #122033;
  }

  span {
    font-size: 14px;
    font-weight: 700;
    color: #6a7b90;
  }
}

.qty-modal__switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #d7e2ee;
  background: #fff;
  cursor: pointer;
  user-select: none;
  text-align: left;

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 700;
    color: #122033;

    .mdi {
      color: #0b4f8a;
      font-size: 18px;
    }
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }

  &.is-on {
    border-color: #0b4f8a;
    background: #e7f1f8;

    .qty-modal__switch-track {
      background: #0b4f8a;

      em {
        transform: translateX(18px);
      }
    }
  }

  &.is-loading {
    .qty-modal__switch-track {
      opacity: 0.7;
    }
  }
}

.qty-modal__switch-track {
  position: relative;
  width: 44px;
  height: 26px;
  border-radius: 999px;
  background: #c9d5e3;
  flex-shrink: 0;
  transition: background 0.15s ease;

  em {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(18, 32, 51, 0.2);
    transition: transform 0.15s ease;
  }
}

.qty-modal__pad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 12px;
}

.qty-modal__key {
  height: 52px;
  border: 1px solid #d7e2ee;
  border-radius: 999px;
  background: #fff;
  color: #122033;
    font-size: 20px;
  font-weight: 750;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;

  &:hover:not(:disabled) {
    background: #e7f1f8;
    border-color: #0b4f8a;
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0;
    pointer-events: none;
  }

  &--action {
    color: #0b4f8a;
  }

  &--ghost {
    visibility: hidden;
  }
}

.qty-modal__actions {
  display: flex;
  gap: 8px;
}

.qty-modal__cancel,
.qty-modal__ok {
  flex: 1;
  min-height: 44px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 750;
  cursor: pointer;
}

.qty-modal__cancel {
  border: 1px solid #d7e2ee;
  background: #fff;
  color: #5b6b82;
}

.qty-modal__ok {
  border: 0;
  background: #0b4f8a;
  color: #fff;

  &:hover {
    background: #093f6e;
  }
}

.pos__cart-item-bottom {
    display: flex;
  justify-content: space-between;
    align-items: center;
  gap: 12px;
  margin-top: 12px;
}

.pos__remove {
  width: 32px;
  height: 32px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  color: #b42318;
    cursor: pointer;

  &:hover {
    background: #fff5f4;
    border-color: #f0b4ae;
  }
}

.pos__qty {
  display: inline-flex;
  align-items: center;
  gap: 8px;

  button {
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 10px;
    background: var(--accent);
    color: #fff;
    font-size: 17px;
    cursor: pointer;
  }

  span {
    min-width: 22px;
    text-align: center;
    font-weight: 750;
  }

  &--input {
    flex-wrap: wrap;

    input {
      width: 110px;
      padding: 8px 10px;
      border: 1px solid var(--line);
      border-radius: 10px;
    }
  }
}

.pos__scale-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: auto !important;
  height: 34px !important;
  padding: 0 10px;
  border: 1px solid #d9e2ec !important;
  border-radius: 10px !important;
  background: #fff !important;
  color: #0b4f8a !important;
  font-size: 12px !important;
  font-weight: 700;
  cursor: pointer;

  .mdi {
    font-size: 16px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }

  &:hover:not(:disabled) {
    background: #eef4fa !important;
  }
}

.pos__cart-sum {
  margin: 0;
  font-weight: 780;
  color: var(--accent);
  font-size: 15px;
}

.pos__cart-old-price {
  color: var(--muted);
  margin-right: 4px;
}

.pos__discount {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;

  > .mdi {
    flex-shrink: 0;
    margin-top: 6px;
    color: var(--accent);
    font-size: 16px;
  }
}

.pos__discount-chips {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}

.pos__discount-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 30px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: #fff;
  color: var(--ink);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  em {
    max-width: 72px;
    overflow: hidden;
    color: var(--muted);
    font-style: normal;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    border-color: #9eb6cc;
  }

  &.is-active {
    background: #0b4f8a;
    border-color: #0b4f8a;
    color: #fff;

    em {
      color: rgba(255, 255, 255, 0.85);
    }
  }
}

.pos__discount-saved {
  flex-shrink: 0;
  align-self: center;
  padding: 3px 8px;
  border-radius: 8px;
  background: #e7f6ec;
  color: #1e7d3e;
  font-size: 12px;
  font-weight: 700;
}

.pos__discount-line {
  margin: 2px 0 0;
  font-size: 13px;
  color: #1e7d3e;

  b {
    font-weight: 750;
  }
}

.pos__checkout {
  margin-top: auto;
  padding-top: 8px;
}

.pos__bonus,
.pos__payment {
  margin-bottom: 12px;
  padding: 14px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
}

.pos__bonus-search {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.pos__phone {
  display: block;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  outline: none;

  &:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(11, 79, 138, 0.1);
  }
}

.pos__bonus-register-btn {
  flex-shrink: 0;
  white-space: nowrap;
}

.pos__bonus-client {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #eef4fa;
  border: 1px solid #d7e4f0;

  strong {
    display: block;
    font-size: 15px;
    font-weight: 750;
    color: var(--ink);
  }

  p {
    margin: 4px 0 0;
    color: var(--muted);
    font-size: 12.5px;
  }

  em {
    flex-shrink: 0;
    font-style: normal;
    font-size: 13px;
    font-weight: 750;
    color: var(--accent);
  }
}

.pos__bonus-info {
  margin: 10px 0 0;
  font-size: 14px;
}

.pos__bonus-miss {
  margin: 10px 0 0;
  color: #b42318;
  font-size: 14px;
}

.pos__link-btn {
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--accent);
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
}

.pos__bonus-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.pos__ghost-btn {
  padding: 8px 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  font-weight: 650;
    cursor: pointer;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: var(--accent-soft);
  }
}

.pos__payment {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pos__payment-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--muted);
}

.pos__pay-types {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pos__pay-type {
  appearance: none;
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink);
  border-radius: 999px;
  padding: 10px 16px;
  font-size: 13.5px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.12s ease;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: var(--accent-soft);
  }

  &.is-active {
    border-color: var(--accent);
    background: var(--accent);
    color: #fff;
  }

  &:active {
    transform: scale(0.97);
  }
}

.pos__payment-amount {
  display: grid;
  gap: 6px;
}

.pos__payment-amount-name {
  font-size: 12.5px;
  font-weight: 700;
  color: var(--muted);
}

.pos__payment-amount-input,
.loan-modal__plain-input,
.loan-modal__input-wrap {
  width: 100%;
  min-height: 48px;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
  color: var(--ink);
  font-size: 18px;
  font-weight: 750;
  outline: none;
}

.loan-modal__input-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;

  input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    font: inherit;
    font-weight: 750;
  }

  em {
    color: var(--muted);
    font-style: normal;
  }
}

.pos__payment-amount-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
  color: var(--ink);
    font-size: 18px;
  font-weight: 750;
  cursor: pointer;
  text-align: left;

  .is-placeholder {
    color: var(--muted);
  }

  .mdi {
    color: var(--accent);
    font-size: 22px;
  }

  &:hover {
    border-color: var(--accent);
  }
}

.pos__payment-fields {
  flex: 1;
}

.pos__payment-tools {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pos__loan-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding: 14px;
  border-radius: 16px;
  border: 1px solid #f0d2b0;
  background:
    linear-gradient(135deg, rgba(255, 247, 237, 0.95), rgba(255, 255, 255, 0.98));
  cursor: pointer;
  transition: border-color 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: #e8b989;
    transform: translateY(-1px);
  }
}

.pos__loan-summary-icon {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #fff1e0;
  color: #b45309;

  .mdi {
    font-size: 22px;
  }
}

.pos__loan-summary-body {
  min-width: 0;
  flex: 1;

  strong {
    display: block;
    font-size: 14px;
    font-weight: 700;
  }

  p {
    margin: 3px 0 0;
    font-size: 12px;
    color: #8a6a4a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.pos__loan-summary-edit {
  width: 34px;
  height: 34px;
  border: 1px solid #edd4b8;
  border-radius: 10px;
  background: #fff;
  color: #9a3412;
  display: grid;
  place-items: center;
  cursor: pointer;
}

.pos__loan,
.pos__loan-title {
  display: none;
}

.pos__round-btn {
  width: 34px;
  height: 34px;
  border: 1px solid var(--line);
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
}

.pos__total {
    display: flex;
  justify-content: flex-end;
    align-items: center;
  gap: 16px;
  margin: 12px 0;
  padding: 16px 18px;
  background: linear-gradient(135deg, #12324f 0%, #0b4f8a 100%);
  border-radius: 16px;
  color: #fff;
  box-shadow: 0 12px 24px rgba(11, 79, 138, 0.22);
  text-align: right;

  .pos__total-meta {
    display: grid;
    gap: 4px;
    justify-items: end;
    min-width: 0;
  }

  span {
    display: block;
    font-size: 12px;
    opacity: 0.78;
    font-weight: 650;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  strong {
    font-size: 28px;
    font-weight: 800;
    letter-spacing: -0.02em;
    white-space: nowrap;
  }
}

.pos__change {
  margin: 0;
  font-size: 13px;
  opacity: 0.92;
  text-align: right;

  b {
    color: #fde68a;
  }
}

.pos__discount-line {
  margin: 0;
  font-size: 12px;
  opacity: 0.9;
  text-align: right;
}

.pos__pay-btn {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border: 0;
  border-radius: 16px;
  background: linear-gradient(180deg, #f59e0b 0%, var(--pay) 100%);
  color: #fff;
  font-size: 17px;
  font-weight: 780;
    cursor: pointer;
  box-shadow: 0 12px 24px rgba(217, 119, 6, 0.28);
  transition: transform 0.15s ease, filter 0.15s ease;

  .mdi {
    font-size: 20px;
  }

  &:hover {
    filter: brightness(1.04);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }
}

.pos__checkout-footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--line);

  .pos__payment {
    margin-bottom: 0;
    padding: 8px;
    border: 0;
    border-radius: 10px;
    background: #f5f8fb;
  }

  .pos__payment-label {
    margin-bottom: 6px;
    text-align: right;
    font-size: 11px;
  }

  .pos__pay-types {
  flex-wrap: wrap;
    justify-content: flex-end;
    gap: 6px;
  }

  .pos__pay-type {
    padding: 7px 10px;
    font-size: 12px;
  }

  .pos__payment-amount {
    margin-top: 6px;
  }

  .pos__payment-amount-name {
    text-align: right;
    font-size: 11px;
  }

  .pos__payment-amount-btn {
    min-height: 36px;
    padding: 8px 10px;
    font-size: 14px;
    border-radius: 10px;
  }

  .pos__total--compact {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    margin: 0;
    padding: 8px 10px;
    border-radius: 10px;
    box-shadow: none;

    span {
      font-size: 10px;
      letter-spacing: 0.03em;
    }

    strong {
      font-size: 18px;
      line-height: 1.15;
    }
  }

  .pos__due-edit {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .pos__due-input {
    width: 110px;
    padding: 4px 8px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    font-size: 18px;
    font-weight: 800;
    text-align: right;
    outline: none;

    &:focus {
      border-color: rgba(255, 255, 255, 0.7);
      background: rgba(255, 255, 255, 0.18);
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    appearance: textfield;
  }

  .pos__due-edit-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    font-size: 18px;
    font-weight: 800;
    cursor: pointer;
  }

  .pos__due-edit em {
    font-style: normal;
    font-size: 16px;
    font-weight: 700;
    opacity: 0.9;
  }

  .pos__change,
  .pos__discount-line {
    font-size: 11px;
    text-align: right;
  }

  .pos__pay-btn--compact {
    width: 100%;
    min-height: 40px;
    padding: 10px 12px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 750;
    box-shadow: 0 6px 14px rgba(217, 119, 6, 0.22);
  }
}

.pos__checkout-fixed,
.pos__checkout-fixed-inner,
.pos__mobile-checkout {
  display: none !important;
}

.pos__dialog {
  border-radius: 16px !important;
}

.pos__date-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  color: var(--accent);
  font-weight: 650;
  cursor: pointer;
}

.pos__mobile-checkout {
  display: none;
}

/* ---------- Планшет (альбомная) ---------- */
@media (max-width: 1180px) {
  .pos__body {
    grid-template-columns: 1.1fr 0.9fr;
    gap: 14px;
    padding: 14px 16px 16px;
  }

  .pos__panel,
  .pos__cart-shell {
    padding: 16px;
  }

  .pos__products {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
}

/* ---------- Планшет (портрет) ---------- */
@media (max-width: 980px) {
  .pos__header {
    flex-wrap: wrap;
    align-items: center;
    row-gap: 10px;
  }

  .pos__brand {
    flex: 1 1 auto;
    min-width: 0;
  }

  .pos__header-actions {
    order: 2;
  }

  .pos__header-meta {
    order: 3;
    flex: 1 1 100%;
    margin-left: 0;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .pos__body {
    grid-template-columns: 1fr;
  }

  .pos__panel,
  .pos__cart-shell {
    min-height: auto;
  }

  .pos__cart-list {
    max-height: none;
  }

  .pos__live {
    display: none;
  }

}

/* ---------- Телефон ---------- */
@media (max-width: 640px) {
  .pos__header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    column-gap: 8px;
    row-gap: 8px;
    padding: 10px 12px;
    padding-top: calc(10px + env(safe-area-inset-top, 0px));
    padding-left: calc(12px + env(safe-area-inset-left, 0px));
    padding-right: calc(12px + env(safe-area-inset-right, 0px));
  }

  .pos__header-actions {
    grid-column: 2;
    grid-row: 1;
    order: 0;
    align-items: center;
  }

  .pos__header-meta {
    grid-column: 1 / -1;
    grid-row: 2;
    order: 0;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 6px;
    width: 100%;
    margin: 0;
    overflow: hidden;
  }

  .pos__paper,
  .pos__touch-toggle,
  .pos__scale-live {
    height: 34px;
    min-height: 34px;
    padding: 0 10px;
    box-sizing: border-box;
  }

  .pos__paper span {
    display: none;
  }

  .pos__paper select {
    max-width: 72px;
  }

  .pos__touch-toggle {
    min-width: 0;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .pos__brand {
    gap: 10px;

    h1 {
      font-size: 18px;
    }

    p {
      display: none;
    }
  }

  .pos__brand-mark {
    width: 38px;
    height: 38px;
    border-radius: 11px;
    font-size: 17px;
  }

  .pos__cashier {
    max-width: 130px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 7px 10px;
  }

  .pos__icon-btn {
    width: 36px;
    height: 36px;
  }

  .pos__header-actions ::v-deep .language-switcher {
    display: inline-flex;
    align-items: center;
    height: 36px;
    box-sizing: border-box;
  }

  .pos__header-actions ::v-deep .language-switcher__btn {
    display: inline-flex;
    align-items: center;
    min-height: 26px;
  }

  .pos__body {
    padding: 10px 10px 16px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    padding-left: calc(10px + env(safe-area-inset-left, 0px));
    padding-right: calc(10px + env(safe-area-inset-right, 0px));
    gap: 10px;
  }

  .pos__mobile-checkout {
    display: none !important;
  }

  .pos__checkout-footer {
    .pos__pay-btn--compact {
      min-height: 44px;
    }
  }

  .pos__panel,
  .pos__cart-shell {
    padding: 14px;
    border-radius: 16px;
  }

  .pos__quick-amount input {
    font-size: 24px;
    padding: 16px 14px;
  }

  .pos__section-head {
    flex-direction: column;
    align-items: stretch;
  }

  .pos__search-wrap {
    width: 100%;
  }

  .pos__catalog-actions {
    width: 100%;
  }

  /* Категории — горизонтальная лента вместо переноса */
  .pos__chips {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 6px;
    margin-bottom: 14px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .pos__chip {
    flex-shrink: 0;
  }

  .pos__products {
    grid-template-columns: repeat(auto-fill, minmax(124px, 1fr));
    gap: 8px;
  }

  .pos__product {
    min-height: 96px;
    padding: 11px;
    gap: 10px;
    border-radius: 13px;
  }

  /* Крупнее кнопки — удобнее пальцем */
  .pos__qty button {
    width: 38px;
    height: 38px;
  }

  .pos__qty--input input {
    width: 92px;
  }

  .pos__remove {
    width: 36px;
    height: 36px;
  }

  .pos__cart-item-bottom {
    flex-wrap: wrap;

    .pos__cart-sum {
      width: 100%;
      text-align: right;
    }
  }

  .pos__pay-btn {
  padding: 15px;
    font-size: 16px;
  }
}
</style>

<style lang="scss">
.pos-loan-dialog {
  box-shadow: none !important;
  background: transparent !important;
  overflow: visible !important;
}

.pos-loan-date {
  box-shadow: none !important;
  background: transparent !important;
}

.loan-modal {
  overflow: hidden;
  border-radius: 22px;
  background:
    radial-gradient(circle at top right, rgba(251, 191, 36, 0.16), transparent 34%),
    linear-gradient(180deg, #fffdf9 0%, #ffffff 48%);
  border: 1px solid #edd6ba;
  box-shadow: 0 24px 60px rgba(61, 36, 16, 0.18);
  color: #1f2937;
}

.loan-modal__header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 22px 22px 8px;

  h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 750;
    letter-spacing: -0.02em;
    color: #1c1917;
  }

  p {
    margin: 4px 0 0;
    font-size: 13px;
    color: #8b7355;
  }
}

.loan-modal__badge {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, #fff7ed, #ffedd5);
  color: #c2410c;
  border: 1px solid #fdba74;

  .mdi {
    font-size: 24px;
  }
}

.loan-modal__close {
  margin-left: auto;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 10px;
  background: #f5efe7;
  color: #78716c;
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover {
    background: #ebe4da;
  }
}

.loan-modal__body {
  padding: 12px 22px 8px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.loan-modal__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.loan-modal__phones {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.loan-modal__phone-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;

  .loan-modal__field {
    flex: 1;
    min-width: 0;
  }
}

.loan-modal__phone-remove {
  width: 42px;
  height: 48px;
  flex-shrink: 0;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
  color: #9a3412;
  cursor: pointer;
  display: grid;
  place-items: center;

  .mdi {
    font-size: 18px;
  }
}

.loan-modal__phone-add {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 12px;
  border: 1px dashed #c3d2e1;
  border-radius: 999px;
  background: transparent;
  color: var(--muted);
  font-weight: 650;
  cursor: pointer;
}

.loan-modal__field {
  display: flex;
  flex-direction: column;
  gap: 7px;

  > span {
    font-size: 12px;
    font-weight: 650;
    color: #78716c;
    letter-spacing: 0.02em;
  }

  input {
    width: 100%;
    padding: 13px 14px;
    border: 1px solid #e7d5c0;
    border-radius: 14px;
    background: #fff;
    outline: none;
    font-size: 15px;
    color: #1c1917;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
      border-color: #d97706;
      box-shadow: 0 0 0 4px rgba(217, 119, 6, 0.12);
    }
  }
}

.loan-modal__input-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 0 14px;
  border: 1px solid #e7d5c0;
  border-radius: 14px;
  background: #fff;
  color: #1c1917;
  text-align: left;
  cursor: pointer;
  font-size: 15px;
  font-weight: 650;

  span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .is-placeholder {
    color: #a8a29e;
    font-weight: 500;
  }

  .mdi {
    color: #d97706;
    font-size: 20px;
    flex-shrink: 0;
  }

  &--amount {
    em {
      font-style: normal;
      font-weight: 700;
      color: #a8a29e;
    }
  }
}

.loan-modal__amount {
  position: relative;

  input {
    padding-right: 42px;
  }

  em {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-style: normal;
    font-weight: 700;
    color: #a8a29e;
  }
}

.loan-modal__phone {
  width: 100%;
  padding: 13px 14px;
  border: 1px solid #e7d5c0;
  border-radius: 14px;
  background: #fff;
  outline: none;
  font-size: 15px;

  &:focus {
    border-color: #d97706;
    box-shadow: 0 0 0 4px rgba(217, 119, 6, 0.12);
  }
}

.loan-modal__date {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 0 14px;
  border: 1px solid #e7d5c0;
  border-radius: 14px;
  background: #fff;
  color: #1c1917;
  cursor: pointer;
  text-align: left;

  .mdi {
    color: #d97706;
    font-size: 20px;
  }

  span {
    font-size: 14px;
    font-weight: 600;
  }
}

.loan-modal__preview {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 14px;
  border-radius: 16px;
  background: #1c1917;
  color: #fff;

  span {
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.62);
  }

  strong {
    font-size: 16px;
    font-weight: 750;
  }
}

.loan-modal__footer {
  display: flex;
  gap: 10px;
  padding: 16px 22px 22px;
}

.loan-modal__btn {
  flex: 1;
  min-height: 46px;
  border: 0;
  border-radius: 14px;
  background: #0b4f8a;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #093f6d;
  }

  &--ghost {
    background: #f5efe7;
    color: #57534e;

    &:hover {
      background: #ebe4da;
    }
  }
}

.loan-date {
  padding: 12px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid #e7d5c0;
  box-shadow: 0 18px 40px rgba(61, 36, 16, 0.16);

  .loan-modal__btn {
    width: 100%;
    margin-top: 10px;
  }
}

@media (max-width: 520px) {
  .loan-modal__row,
  .loan-modal__preview {
    grid-template-columns: 1fr;
  }
}

.pos-bonus-client-dialog {
  box-shadow: none !important;
  background: transparent !important;
  overflow: visible !important;
}

.bonus-client-modal {
  overflow: hidden;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(18, 32, 51, 0.18);
  color: #152033;
}

.bonus-client-modal__header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 20px 0;
}

.bonus-client-modal__badge {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: #eef4fa;
  color: #0b4f8a;

  .mdi {
    font-size: 22px;
  }
}

.bonus-client-modal__header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 750;
}

.bonus-client-modal__header p {
  margin: 4px 0 0;
  color: #6b7a90;
  font-size: 13px;
}

.bonus-client-modal__close {
  margin-left: auto;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 10px;
  background: #f3f6fa;
  color: #6b7a90;
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover {
    background: #e8eef5;
    color: #152033;
  }
}

.bonus-client-modal__body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
}

.bonus-client-modal__field {
  display: block;

  > span {
    display: block;
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 700;
  }

  input,
  .bonus-client-modal__phone {
    width: 100%;
    padding: 14px 16px;
    border: 1.5px solid #d9e2ec;
    border-radius: 14px;
    background: #fbfdff;
    color: #152033;
    font-size: 15px;
    font-weight: 600;
    outline: none;

    &:focus {
      border-color: #0b4f8a;
      box-shadow: 0 0 0 4px rgba(11, 79, 138, 0.1);
    }
  }
}

.bonus-client-modal__card {
  display: flex;
  gap: 8px;

  input {
    flex: 1;
    letter-spacing: 0.12em;
    font-size: 20px;
    font-weight: 800;
    background: #eef4fa;
  }
}

.bonus-client-modal__refresh {
  width: 48px;
  border: 1.5px solid #d9e2ec;
  border-radius: 14px;
  background: #fff;
  color: #0b4f8a;
  cursor: pointer;

  .mdi {
    font-size: 20px;
  }

  &:hover:not(:disabled) {
    background: #eef4fa;
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
}

.bonus-client-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 20px 20px;
}

.bonus-client-modal__btn {
  min-width: 120px;
  padding: 12px 16px;
  border: 0;
  border-radius: 12px;
  background: #0b4f8a;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #093f6d;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &--ghost {
    background: #f3f6fa;
    color: #3d4f63;

    &:hover:not(:disabled) {
      background: #e8eef5;
    }
  }
}

.receipt-dialog {
  border-radius: 20px;
  box-shadow: 0 24px 70px rgba(15, 35, 56, .28) !important;
}

.receipt-modal {
  position: relative;
  overflow: hidden;
  background: #f2f5f8;
}

.receipt-modal__close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.92);
  color: #3d4f63;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(15, 35, 56, 0.12);

  .mdi {
    font-size: 20px;
  }

  &:hover {
    color: #0b4f8a;
  }
}

.receipt {
  padding: 28px 24px 24px;
  background: #fff;
  color: #172033;
  font-family: "Roboto Mono", "Courier New", monospace;
  font-size: 13px;
}

.receipt__header {
  display: grid;
  justify-items: center;
  gap: 5px;
  text-align: center;

  strong {
    font-family: Arial, sans-serif;
    font-size: 22px;
    letter-spacing: -.03em;
  }

  p,
  time {
    margin: 0;
    color: #66758a;
    font-size: 12px;
  }
}

.receipt__mark {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 10px;
  background: #0b4f8a;
  color: #fff;
  font-family: Arial, sans-serif;
  font-size: 18px;
  font-weight: 800;
}

.receipt__dash {
  margin: 18px 0;
  border-top: 1px dashed #9aa9b9;
}

.receipt__quick {
  padding: 10px 0;
  text-align: center;
  color: #45576c;
}

.receipt__items {
  display: grid;
  gap: 13px;
}

.receipt__item {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 3px 12px;

  strong {
    grid-column: 1 / -1;
    font-family: Arial, sans-serif;
    font-size: 14px;
  }

  span {
    color: #66758a;
  }

  b {
    font-weight: 750;
  }
}

.receipt__mod {
  grid-column: 1 / -1;
  font-size: 12px;
  color: #66758a;
}

.receipt__payments {
  display: grid;
  gap: 8px;

  > div,
  .receipt__total {
    display: flex;
    justify-content: space-between;
    gap: 16px;
  }
}

.receipt__total {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 2px solid #152033;
  font-family: Arial, sans-serif;

  strong {
    font-size: 22px;
  }
}

.receipt__footer {
  display: grid;
  gap: 11px;
  margin-top: 22px;
  color: #66758a;
  text-align: center;
  font-size: 12px;

  strong {
    color: #0b4f8a;
    font-family: Arial, sans-serif;
  }
}

.receipt-modal__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  background: #f2f5f8;
}

.receipt-modal__auto {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  margin: 0 0 4px;
  font-size: 13px;
  color: #52627a;
  cursor: pointer;
}

.receipt-modal__online {
  color: #0f766e;
  font-weight: 600;
}

.receipt-modal__btn {
  flex: 1;
  min-width: 110px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 42px;
  border: 0;
  border-radius: 10px;
  background: #0b4f8a;
  color: #fff;
  font-weight: 700;
  cursor: pointer;

  &--ghost {
    background: #e4ebf2;
    color: #34485e;
  }
}
</style>